// ============================================================================
// ENTERPRISE POKER COACH SERVICE v2.0
// ============================================================================
// Advanced AI-powered poker coaching with deep statistical analysis,
// personalized feedback, leak detection, and intelligent query handling.
// No LLM needed - all responses are algorithmically generated with
// professional-grade poker knowledge.
// ============================================================================

import { 
  pokerMath,
  calculateEquity,
  calculatePotOdds,
  isCallProfitable,
  calculateBetSizing,
  getOpeningRange,
  isHandInRange,
  calculateMDF,
  getHeadsUpEquity,
  DRAW_OUTS,
} from './pokerMathEngine';

import {
  SCENARIOS,
  getRandomScenario,
  getScenariosByConcept,
  getScenarioById,
  formatScenarioForDisplay,
  type Scenario,
  type ScenarioConcept,
  type ScenarioDifficulty,
} from './scenarioLibrary';

import { POKER_GLOSSARY } from './trainingAnalysisService';
import type { TrainingReport } from './trainingAnalysisService';
import type { LoggedDecision } from './trainingDecisionLogger';

// ============================================================================
// OPTIMAL POKER STATISTICS - Research-Based Benchmarks
// ============================================================================

export const OPTIMAL_STATS = {
  // 6-Max Cash Game Optimal Ranges (based on winning player data)
  sixMax: {
    vpip: { min: 19, optimal: 24, max: 28, description: 'Voluntarily Put $ In Pot' },
    pfr: { min: 16, optimal: 20, max: 24, description: 'Pre-Flop Raise %' },
    vpipPfrGap: { min: 2, optimal: 4, max: 6, description: 'Gap between VPIP and PFR' },
    aggFactor: { min: 2.0, optimal: 2.8, max: 3.5, description: 'Aggression Factor (Bet+Raise)/Call' },
    cbet: { min: 50, optimal: 65, max: 75, description: 'Continuation Bet %' },
    foldToCbet: { min: 40, optimal: 50, max: 60, description: 'Fold to C-Bet %' },
    wtsd: { min: 24, optimal: 27, max: 32, description: 'Went to Showdown %' },
    wsd: { min: 49, optimal: 53, max: 58, description: 'Won $ at Showdown %' },
    wwsf: { min: 45, optimal: 48, max: 53, description: 'Won When Saw Flop %' },
    threeBet: { min: 5, optimal: 7, max: 10, description: '3-Bet Preflop %' },
    foldTo3Bet: { min: 45, optimal: 55, max: 65, description: 'Fold to 3-Bet %' },
    steal: { min: 28, optimal: 35, max: 45, description: 'Attempt to Steal %' },
    foldToSteal: { min: 60, optimal: 70, max: 80, description: 'Fold to Steal %' },
  },
  // Full Ring (9-Max) Adjustments
  fullRing: {
    vpip: { min: 12, optimal: 16, max: 20 },
    pfr: { min: 10, optimal: 14, max: 17 },
  },
  // Position-Based VPIP Ranges (6-Max)
  positionVpip: {
    'UTG': { min: 12, optimal: 15, max: 18 },
    'UTG+1': { min: 14, optimal: 17, max: 20 },
    'MP': { min: 16, optimal: 20, max: 24 },
    'CO': { min: 22, optimal: 27, max: 32 },
    'BTN': { min: 35, optimal: 42, max: 50 },
    'SB': { min: 28, optimal: 35, max: 42 },
    'BB': { min: 35, optimal: 45, max: 55 }, // Including defense
  },
};

// ============================================================================
// PLAYER TYPE CLASSIFICATION
// ============================================================================

export type PlayerType = 
  | 'rock' 
  | 'nit' 
  | 'tag' 
  | 'lag' 
  | 'calling_station' 
  | 'fish' 
  | 'maniac' 
  | 'unknown';

export interface PlayerProfile {
  type: PlayerType;
  label: string;
  description: string;
  strengths: string[];
  weaknesses: string[];
  exploitStrategy: string[];
}

export function classifyPlayerType(vpip: number, pfr: number, aggFactor: number): PlayerProfile {
  const gap = vpip - pfr;
  
  // Rock: Ultra-tight, plays almost nothing
  if (vpip < 10) {
    return {
      type: 'rock',
      label: 'Rock',

      description: 'Extremely tight player who only plays premium hands',
      strengths: ['Rarely makes mistakes with bad hands', 'Easy to put on a range'],
      weaknesses: ['Too predictable', 'Misses value', 'Easy to bluff'],
      exploitStrategy: [
        'Fold to their raises unless you have a monster',
        'Steal their blinds relentlessly',
        'Bluff them on scary boards',
      ],
    };
  }
  
  // Nit: Very tight, but plays a bit more
  if (vpip < 15 && gap < 3) {
    return {
      type: 'nit',
      label: 'Nit',

      description: 'Very tight player with minimal looseness',
      strengths: ['Disciplined preflop', 'Patient'],
      weaknesses: ['Exploitable by aggression', 'Predictable ranges'],
      exploitStrategy: [
        'Steal their blinds frequently',
        'Apply pressure with medium-strength hands',
        'Fold to their aggression without the nuts',
      ],
    };
  }
  
  // TAG: Tight-Aggressive (optimal style)
  if (vpip >= 18 && vpip <= 26 && gap <= 5 && aggFactor >= 2.5) {
    return {
      type: 'tag',
      label: 'TAG',

      description: 'Tight-Aggressive - solid, winning player style',
      strengths: ['Balanced ranges', 'Aggressive postflop', 'Hard to exploit'],
      weaknesses: ['May be too predictable in 3-bet pots', 'Can be outplayed by LAGs'],
      exploitStrategy: [
        'Play straightforward against them',
        'Look for small edges in 3-bet pots',
        'Avoid big confrontations without strong hands',
      ],
    };
  }
  
  // LAG: Loose-Aggressive
  if (vpip >= 27 && vpip <= 35 && gap <= 7 && aggFactor >= 3) {
    return {
      type: 'lag',
      label: 'LAG',

      description: 'Loose-Aggressive - skilled, unpredictable player',
      strengths: ['Wide ranges', 'Applies pressure', 'Wins pots without showdown'],
      weaknesses: ['Can overplay hands', 'Susceptible to traps'],
      exploitStrategy: [
        'Tighten up preflop',
        'Call down lighter',
        'Let them hang themselves with bluffs',
        'Trap with strong hands',
      ],
    };
  }
  
  // Calling Station: Loose-Passive
  if (vpip >= 35 && gap >= 15 && aggFactor < 1.5) {
    return {
      type: 'calling_station',
      label: 'Calling Station',

      description: 'Loose player who calls too much and rarely folds',
      strengths: ['Occasionally hits hidden hands'],
      weaknesses: ['Calls way too much', 'Easy to value bet', 'Never bluffs'],
      exploitStrategy: [
        'NEVER bluff them',
        'Value bet relentlessly with any made hand',
        'Bet bigger for value - they\'ll call anyway',
        'Don\'t try to push them off hands',
      ],
    };
  }
  
  // Fish: Loose-Passive recreational player
  if (vpip >= 40 && aggFactor < 2) {
    return {
      type: 'fish',
      label: 'Fish',

      description: 'Recreational player who plays too many hands passively',
      strengths: ['Occasionally gets lucky'],
      weaknesses: ['Plays too many hands', 'Passive postflop', 'Doesn\'t fold'],
      exploitStrategy: [
        'Value bet mercilessly',
        'Avoid bluffing',
        'Isolate them preflop',
        'Play position against them',
      ],
    };
  }
  
  // Maniac: Ultra loose-aggressive
  if (vpip >= 40 && aggFactor >= 4) {
    return {
      type: 'maniac',
      label: '🃏 Maniac',
      description: 'Hyper-aggressive player who plays almost everything',
      strengths: ['Unpredictable', 'Puts maximum pressure'],
      weaknesses: ['Overplays weak hands', 'Easy to trap'],
      exploitStrategy: [
        'Tighten up significantly',
        'Call down with medium-strength hands',
        'Let them bluff into you',
        'Don\'t try to outbluff them',
      ],
    };
  }
  
  return {
    type: 'unknown',
    label: 'Unknown',

    description: 'Need more hands to classify',
    strengths: [],
    weaknesses: [],
    exploitStrategy: ['Gather more information'],
  };
}

// ============================================================================
// LEAK DETECTION SYSTEM
// ============================================================================

export interface Leak {
  id: string;
  severity: 'critical' | 'major' | 'minor';
  category: 'preflop' | 'postflop' | 'aggression' | 'showdown' | 'position';
  title: string;
  description: string;
  impact: string;
  currentValue: number;
  optimalRange: { min: number; max: number };
  fix: string[];
  drills: string[];
  priority: number;
}

export function detectLeaks(report: TrainingReport): Leak[] {
  const leaks: Leak[] = [];
  const stats = report.stats;
  const opt = OPTIMAL_STATS.sixMax;
  
  // ===================
  // VPIP Analysis
  // ===================
  if (stats.vpip > opt.vpip.max + 10) {
    leaks.push({
      id: 'vpip-way-too-high',
      severity: 'critical',
      category: 'preflop',
      title: 'Playing Way Too Many Hands',
      description: `Your VPIP of ${stats.vpip.toFixed(0)}% is extremely high. You're playing almost every hand.`,
      impact: 'You\'re entering pots with weak hands that can\'t win, bleeding money preflop.',
      currentValue: stats.vpip,
      optimalRange: { min: opt.vpip.min, max: opt.vpip.max },
      fix: [
        'Only play premium hands from early position (AA, KK, QQ, AK)',
        'Tighten up significantly - fold 72o, J4o, K3o immediately',
        'Wait for position before playing speculative hands',
      ],
      drills: [
        ' EP Challenge: Only open AA-TT, AK, AQ from UTG/UTG+1',

        ' Fold Practice: Fold every hand except top 15% for 20 hands',

      ],
      priority: 1,
    });
  } else if (stats.vpip > opt.vpip.max) {
    leaks.push({
      id: 'vpip-too-high',
      severity: 'major',
      category: 'preflop',
      title: 'Playing Too Loose Preflop',
      description: `Your VPIP of ${stats.vpip.toFixed(0)}% is above optimal (${opt.vpip.max}%).`,
      impact: 'Entering too many pots with marginal hands costs you money long-term.',
      currentValue: stats.vpip,
      optimalRange: { min: opt.vpip.min, max: opt.vpip.max },
      fix: [
        'Tighten your opening ranges by ~5%',
        'Fold more from early and middle position',
        'Be more selective with suited connectors',
      ],
      drills: [
        ' Track which hands you\'re losing with most',

        ' Only open top 20% from UTG for one session',

      ],
      priority: 2,
    });
  } else if (stats.vpip < opt.vpip.min - 5) {
    leaks.push({
      id: 'vpip-too-low',
      severity: 'major',
      category: 'preflop',
      title: 'Playing Too Tight',
      description: `Your VPIP of ${stats.vpip.toFixed(0)}% is very low. You're only playing premium hands.`,
      impact: 'You\'re missing profitable situations and becoming too predictable.',
      currentValue: stats.vpip,
      optimalRange: { min: opt.vpip.min, max: opt.vpip.max },
      fix: [
        'Open more hands from late position',
        'Add suited connectors and suited aces to your range',
        'Steal blinds more frequently from BTN and CO',
      ],
      drills: [
        ' BTN Challenge: Open 40%+ of hands when folded to you on Button',

        ' Add 87s, 76s, A5s to your CO/BTN range',

      ],
      priority: 3,
    });
  }
  
  // ===================
  // VPIP/PFR Gap Analysis
  // ===================
  const gap = stats.vpip - stats.pfr;
  if (gap > opt.vpipPfrGap.max + 5) {
    leaks.push({
      id: 'gap-too-wide',
      severity: 'critical',
      category: 'preflop',
      title: 'Calling Too Much Preflop',
      description: `Your VPIP/PFR gap of ${gap.toFixed(0)} is way too wide. You're limping and calling too much.`,
      impact: 'Passive preflop play bleeds money. You give up initiative and play guessing games postflop.',
      currentValue: gap,
      optimalRange: { min: opt.vpipPfrGap.min, max: opt.vpipPfrGap.max },
      fix: [
        'NEVER open-limp. If a hand is worth playing, raise it.',
        'Raise or fold - stop flatting so many opens',
        'When you call, have a plan for the flop',
      ],
      drills: [
        ' Zero Limp Challenge: Do not limp a single hand for the entire session',

        ' Raise-or-Fold: For one orbit, only raise or fold preflop',

      ],
      priority: 1,
    });
  } else if (gap > opt.vpipPfrGap.max) {
    leaks.push({
      id: 'gap-wide',
      severity: 'major',
      category: 'preflop',
      title: 'Preflop Passivity',
      description: `Your VPIP/PFR gap of ${gap.toFixed(0)} suggests you\'re calling too much preflop.`,
      impact: 'Calling preflop puts you at a disadvantage - you don\'t have initiative.',
      currentValue: gap,
      optimalRange: { min: opt.vpipPfrGap.min, max: opt.vpipPfrGap.max },
      fix: [
        'Raise more hands instead of calling',
        'Either raise or fold in most spots',
        '3-bet more frequently when facing opens',
      ],
      drills: [
        ' Tighten calls: Only flat with pairs 22-TT or suited connectors in position',

      ],
      priority: 2,
    });
  }
  
  // ===================
  // C-Bet Analysis
  // ===================
  if (stats.cbetFrequency < 40) {
    leaks.push({
      id: 'cbet-too-low',
      severity: 'critical',
      category: 'postflop',
      title: 'Not C-Betting Enough',
      description: `Your C-Bet of ${stats.cbetFrequency.toFixed(0)}% is way too low. You're giving up on pots you should win.`,
      impact: 'You miss easy money. As the preflop aggressor, you should bet most flops.',
      currentValue: stats.cbetFrequency,
      optimalRange: { min: opt.cbet.min, max: opt.cbet.max },
      fix: [
        'C-bet 100% on dry boards like K72r or A62r',
        'C-bet at least 60% on most boards heads-up',
        'Use smaller sizing (1/3 pot) to c-bet more often',
      ],
      drills: [
        ' C-Bet Challenge: Bet 60% pot on every dry flop as preflop raiser',

        ' Auto C-Bet: Bet any flop with an Ace or King when heads-up',

      ],
      priority: 1,
    });
  } else if (stats.cbetFrequency < opt.cbet.min) {
    leaks.push({
      id: 'cbet-low',
      severity: 'major',
      category: 'postflop',
      title: 'C-Betting Too Infrequently',
      description: `Your C-Bet of ${stats.cbetFrequency.toFixed(0)}% is below optimal.`,
      impact: 'You\'re missing value and letting opponents realize their equity.',
      currentValue: stats.cbetFrequency,
      optimalRange: { min: opt.cbet.min, max: opt.cbet.max },
      fix: [
        'Increase c-bet frequency on favorable boards',
        'Use smaller bet sizes to c-bet more hands',
      ],
      drills: [
        ' Bet the flop with any pair or better plus all overcards',

      ],
      priority: 3,
    });
  } else if (stats.cbetFrequency > opt.cbet.max + 10) {
    leaks.push({
      id: 'cbet-too-high',
      severity: 'major',
      category: 'postflop',
      title: 'C-Betting Too Much',
      description: `Your C-Bet of ${stats.cbetFrequency.toFixed(0)}% is too high. You're betting every flop.`,
      impact: 'Observant opponents will start check-raising and floating you.',
      currentValue: stats.cbetFrequency,
      optimalRange: { min: opt.cbet.min, max: opt.cbet.max },
      fix: [
        'Check back on boards that favor the caller (765, 986)',
        'Check back weak hands that have showdown value',
        'Balance your checking range on wet boards',
      ],
      drills: [
        ' Check practice: Check back at least 30% of flops on wet boards',

      ],
      priority: 3,
    });
  }
  
  // ===================
  // Aggression Factor Analysis
  // ===================
  if (stats.aggressionFactor < 1.5) {
    leaks.push({
      id: 'agg-way-too-low',
      severity: 'critical',
      category: 'aggression',
      title: 'Way Too Passive Postflop',
      description: `Your Aggression Factor of ${stats.aggressionFactor.toFixed(1)} is extremely low.`,
      impact: 'You\'re calling when you should be betting/raising. Passive poker is losing poker.',
      currentValue: stats.aggressionFactor,
      optimalRange: { min: opt.aggFactor.min, max: opt.aggFactor.max },
      fix: [
        'BET your good hands! Stop checking top pair',
        'Raise when you think you\'re ahead',
        'Semi-bluff with draws instead of just calling',
      ],
      drills: [
        ' Value Town: Always bet top pair or better on river',

        ' Raise Challenge: Raise instead of calling with strong draws',

      ],
      priority: 1,
    });
  } else if (stats.aggressionFactor < opt.aggFactor.min) {
    leaks.push({
      id: 'agg-low',
      severity: 'major',
      category: 'aggression',
      title: 'Too Passive Postflop',
      description: `Your Aggression Factor of ${stats.aggressionFactor.toFixed(1)} is below optimal.`,
      impact: 'You\'re not extracting enough value from your hands.',
      currentValue: stats.aggressionFactor,
      optimalRange: { min: opt.aggFactor.min, max: opt.aggFactor.max },
      fix: [
        'Bet for value more often with strong hands',
        'Add more semi-bluffs to your range',
        'Raise more on the flop and turn',
      ],
      drills: [
        ' 3-barrel practice: Follow through on turn and river with value hands',

      ],
      priority: 2,
    });
  } else if (stats.aggressionFactor > opt.aggFactor.max + 1) {
    leaks.push({
      id: 'agg-too-high',
      severity: 'major',
      category: 'aggression',
      title: 'Overly Aggressive',
      description: `Your Aggression Factor of ${stats.aggressionFactor.toFixed(1)} is too high.`,
      impact: 'You\'re betting/raising too much and may be bluffing unprofitably.',
      currentValue: stats.aggressionFactor,
      optimalRange: { min: opt.aggFactor.min, max: opt.aggFactor.max },
      fix: [
        'Call more with medium-strength hands',
        'Reduce bluff frequency, especially on later streets',
        'Pot control with one-pair hands',
      ],
      drills: [
        ' Call-down practice: Just call with marginal hands instead of raising',

      ],
      priority: 3,
    });
  }
  
  // ===================
  // Position Analysis
  // ===================
  if (report.positionStats) {
    const blindsPlayed = (report.positionStats.SB || 0) + (report.positionStats.BB || 0);
    const totalHands = Object.values(report.positionStats).reduce((a, b) => a + b, 0);
    const blindsPercent = (blindsPlayed / totalHands) * 100;
    
    if (blindsPercent > 50) {
      leaks.push({
        id: 'blinds-too-active',
        severity: 'major',
        category: 'position',
        title: 'Overplaying From Blinds',
        description: `You're playing ${blindsPercent.toFixed(0)}% of hands from the blinds.`,
        impact: 'The blinds are the worst positions - you should be folding more.',
        currentValue: blindsPercent,
        optimalRange: { min: 30, max: 45 },
        fix: [
          'Tighten your big blind defense range',
          'Stop completing from the small blind',
          'Only 3-bet or fold from SB vs late position opens',
        ],
        drills: [
          ' SB Discipline: Only 3-bet or fold from SB for one session',

        ],
        priority: 2,
      });
    }
  }
  
  // Sort by priority
  return leaks.sort((a, b) => a.priority - b.priority);
}

// ============================================================================
// PERSONALIZED IMPROVEMENT PLAN
// ============================================================================

export interface ImprovementPlan {
  overallAssessment: string;
  playerType: PlayerProfile;
  skillLevel: string;
  strengthAreas: string[];
  focusAreas: string[];
  leaks: Leak[];
  weeklyGoals: string[];
  recommendedScenarios: ScenarioConcept[];
  progressMetrics: {
    metric: string;
    current: number;
    target: number;
    priority: 'high' | 'medium' | 'low';
  }[];
}

export function generateImprovementPlan(report: TrainingReport): ImprovementPlan {
  const leaks = detectLeaks(report);
  const playerType = classifyPlayerType(
    report.stats.vpip,
    report.stats.pfr,
    report.stats.aggressionFactor
  );
  
  // Determine focus areas based on leaks
  const focusAreas: string[] = [];
  const recommendedScenarios: ScenarioConcept[] = [];
  
  const criticalLeaks = leaks.filter(l => l.severity === 'critical');
  const majorLeaks = leaks.filter(l => l.severity === 'major');
  
  if (criticalLeaks.some(l => l.category === 'preflop')) {
    focusAreas.push('Preflop hand selection is your biggest leak');
    recommendedScenarios.push('preflop-ranges');
  }
  if (criticalLeaks.some(l => l.category === 'postflop')) {
    focusAreas.push('Continuation betting needs immediate work');
    recommendedScenarios.push('continuation-bet');
  }
  if (criticalLeaks.some(l => l.category === 'aggression')) {
    focusAreas.push('You need to be more aggressive with your strong hands');
    recommendedScenarios.push('value-betting');
  }
  if (leaks.some(l => l.id.includes('gap'))) {
    focusAreas.push('Stop calling - raise or fold');
    recommendedScenarios.push('position');
  }
  
  // Strength areas
  const strengthAreas: string[] = [];
  const opt = OPTIMAL_STATS.sixMax;
  
  if (report.stats.vpip >= opt.vpip.min && report.stats.vpip <= opt.vpip.max) {
    strengthAreas.push('VPIP is in optimal range');
  }
  if (report.stats.pfr >= opt.pfr.min && report.stats.pfr <= opt.pfr.max) {
    strengthAreas.push('PFR shows good aggression preflop');
  }
  if (report.stats.cbetFrequency >= opt.cbet.min && report.stats.cbetFrequency <= opt.cbet.max) {
    strengthAreas.push('C-bet frequency is solid');
  }
  if (report.stats.aggressionFactor >= opt.aggFactor.min && report.stats.aggressionFactor <= opt.aggFactor.max) {
    strengthAreas.push('Postflop aggression is well-balanced');
  }
  
  // Weekly goals
  const weeklyGoals: string[] = [];
  if (criticalLeaks.length > 0) {
    weeklyGoals.push(`Fix critical leak: ${criticalLeaks[0].title}`);
  }
  if (majorLeaks.length > 0) {
    weeklyGoals.push(`Work on: ${majorLeaks[0].title}`);
  }
  weeklyGoals.push('Play at least 3 training sessions');
  weeklyGoals.push('Review your biggest losing hands');
  
  // Progress metrics
  const progressMetrics = [
    {
      metric: 'VPIP',
      current: report.stats.vpip,
      target: opt.vpip.optimal,
      priority: Math.abs(report.stats.vpip - opt.vpip.optimal) > 10 ? 'high' : 'medium' as 'high' | 'medium' | 'low',
    },
    {
      metric: 'PFR',
      current: report.stats.pfr,
      target: opt.pfr.optimal,
      priority: Math.abs(report.stats.pfr - opt.pfr.optimal) > 8 ? 'high' : 'medium' as 'high' | 'medium' | 'low',
    },
    {
      metric: 'C-Bet %',
      current: report.stats.cbetFrequency,
      target: opt.cbet.optimal,
      priority: report.stats.cbetFrequency < 40 ? 'high' : 'medium' as 'high' | 'medium' | 'low',
    },
    {
      metric: 'Aggression',
      current: report.stats.aggressionFactor,
      target: opt.aggFactor.optimal,
      priority: report.stats.aggressionFactor < 2 ? 'high' : 'low' as 'high' | 'medium' | 'low',
    },
  ];
  
  // Overall assessment
  let overallAssessment = '';
  if (criticalLeaks.length >= 2) {
    overallAssessment = `You have ${criticalLeaks.length} critical leaks that are costing you significant money. Focus on fixing these before anything else.`;
  } else if (criticalLeaks.length === 1) {
    overallAssessment = `You have one major issue: ${criticalLeaks[0].title}. Fixing this will dramatically improve your results.`;
  } else if (majorLeaks.length > 0) {
    overallAssessment = `Your fundamentals are decent but you have room for improvement in ${majorLeaks.length} areas.`;
  } else {
    overallAssessment = `Your stats are looking solid! Focus on fine-tuning and maintaining consistency.`;
  }
  
  return {
    overallAssessment,
    playerType,
    skillLevel: report.skillLevel,
    strengthAreas,
    focusAreas,
    leaks,
    weeklyGoals,
    recommendedScenarios,
    progressMetrics,
  };
}

// ============================================================================
// TYPES
// ============================================================================

export type QueryType = 
  | 'definition'
  | 'hand_review'
  | 'should_i'
  | 'scenario_request'
  | 'range_question'
  | 'odds_calculation'
  | 'equity_calculation'
  | 'session_review'
  | 'challenge_help'
  | 'stat_analysis'
  | 'leak_detection'
  | 'improvement_plan'
  | 'position_advice'
  | 'opponent_analysis'
  | 'bet_sizing'
  | 'general_advice'
  | 'unknown'
  // NEW: Platform-specific query types
  | 'platform_help'
  | 'training_rewards'
  | 'daily_challenge'
  | 'short_stack'
  | 'tournament_icm'
  // NEW: Player profile questions
  | 'player_profile';

// ============================================================================
// QUERY SYNONYM MAP - Makes coach understand casual/slang terms
// ============================================================================

export const QUERY_SYNONYMS: Record<string, string> = {
  // ========== ACTION SYNONYMS ==========
  'shove': 'all-in',
  'jam': 'all-in',
  'push': 'all-in',
  'ship': 'all-in',
  'ship it': 'all-in',
  'rip it': 'all-in',
  'flat': 'call',
  'peel': 'call',
  'float': 'call and bluff later',
  'cbet': 'continuation bet',
  'c-bet': 'continuation bet',
  'c bet': 'continuation bet',
  'stab': 'bet',
  'barrel': 'bet again',
  'double barrel': 'bet turn after betting flop',
  'triple barrel': 'bet all three streets',
  'fire': 'bet',
  'lead': 'donk bet',
  'donk': 'donk bet',
  'probe': 'probe bet',
  'block': 'block bet',
  '3b': '3-bet',
  '3bet': '3-bet',
  'three bet': '3-bet',
  'three-bet': '3-bet',
  'threeb': '3-bet',
  '4b': '4-bet',
  '4bet': '4-bet',
  'four bet': '4-bet',
  'four-bet': '4-bet',
  'iso': 'isolate raise',
  'squeeze': 'squeeze play',
  'sqz': 'squeeze play',
  'x/r': 'check-raise',
  'xr': 'check-raise',
  'x/c': 'check-call',
  'xc': 'check-call',
  'x/f': 'check-fold',
  'xf': 'check-fold',
  'b/f': 'bet-fold',
  'b/c': 'bet-call',
  'overbet': 'bet more than pot',
  
  // ========== POSITION SYNONYMS ==========
  'btn': 'button',
  'bu': 'button',
  'otb': 'button',
  'd': 'button',
  'dealer': 'button',
  'sb': 'small blind',
  'bb': 'big blind',
  'utg': 'under the gun',
  'utg+1': 'under the gun plus one',
  'utg1': 'under the gun plus one',
  'ep': 'early position',
  'mp': 'middle position',
  'lp': 'late position',
  'co': 'cutoff',
  'cut off': 'cutoff',
  'hjack': 'hijack',
  'hj': 'hijack',
  'lojack': 'lojack',
  'lj': 'lojack',
  'ip': 'in position',
  'oop': 'out of position',
  
  // ========== HAND TYPE SYNONYMS ==========
  'pp': 'pocket pair',
  'sc': 'suited connectors',
  'suited gapper': 'suited one-gapper',
  'broadways': 'broadway cards',
  'broadway': 'broadway cards',
  'premiums': 'premium hands',
  'premium': 'premium hands',
  'trash': 'weak hands',
  'rags': 'weak hands',
  'garbage': 'weak hands',
  'junk': 'weak hands',
  'connectors': 'connected cards',
  'gappers': 'gapped connectors',
  'wheel': 'A2345 straight',
  'wheel cards': 'A-5 suited',
  
  // ========== BOARD TEXTURE SYNONYMS ==========
  'wet': 'wet board',
  'dry': 'dry board',
  'drawy': 'wet board',
  'draw heavy': 'wet board',
  'rainbow': 'rainbow board',
  'monotone': 'monotone board',
  'two-tone': 'two-tone board',
  'paired': 'paired board',
  'coordinated': 'coordinated board',
  'disconnected': 'dry board',
  'static': 'dry board',
  'dynamic': 'wet board',
  
  // ========== DRAW TYPE SYNONYMS ==========
  'fd': 'flush draw',
  'flushdraw': 'flush draw',
  'oesd': 'open-ended straight draw',
  'oesfd': 'open-ended straight flush draw',
  'gutter': 'gutshot',
  'gutty': 'gutshot',
  'belly buster': 'gutshot',
  'inside straight': 'gutshot',
  'bdfd': 'backdoor flush draw',
  'backdoor': 'backdoor draw',
  'combo': 'combo draw',
  'combo draw': 'flush draw plus straight draw',
  'nfd': 'nut flush draw',
  'nut flush': 'nut flush draw',
  
  // ========== PLAYER TYPE SYNONYMS ==========
  // Note: We keep core player type terms (fish, nit, tag, lag, etc.) 
  // unchanged for pattern matching. Only normalize alternate spellings.
  'station': 'calling station',
  'call station': 'calling station',
  'drooler': 'fish',
  'punter': 'fish',
  'whale': 'fish',
  'donk': 'donkey',
  'grinder': 'reg',
  'regular': 'reg',
  'loose passive': 'calling station',
  'tight passive': 'nit',
  'tight aggressive': 'tag',
  'loose aggressive': 'lag',
  'hyper aggressive': 'maniac',
  
  // ========== STAT SYNONYMS ==========
  'vpip': 'voluntarily put money in pot',
  'pfr': 'preflop raise percentage',
  'af': 'aggression factor',
  'agg': 'aggression factor',
  'aggression': 'aggression factor',
  'wtsd': 'went to showdown',
  'wsd': 'won at showdown',
  'w$sd': 'won money at showdown',
  'wwsf': 'won when saw flop',
  'wr': 'win rate',
  'winrate': 'win rate',
  'bb/100': 'big blinds per 100 hands',
  'roi': 'return on investment',
  'itm': 'in the money',
  'ft': 'final table',
  
  // ========== PLATFORM SYNONYMS ==========
  'cash': 'pkcash',
  'pk cash': 'pkcash',
  'pk': 'pkcash',
  'real money': 'pkcash',
  'coins': 'playcoins',
  'play coins': 'playcoins',
  'free chips': 'playcoins',
  'practice chips': 'playcoins',
  'withdraw': 'withdrawal',
  'cashout': 'withdrawal',
  'cash out': 'withdrawal',
  'buyin': 'buy in',
  'buy-in': 'buy in',
  
  // ========== SITUATION SYNONYMS ==========
  'hu': 'heads up',
  'heads-up': 'heads up',
  'headsup': 'heads up',
  '3way': 'three way',
  '3-way': 'three way',
  'multiway': 'multi-way pot',
  'mw': 'multi-way pot',
  'srp': 'single raised pot',
  '3bp': '3-bet pot',
  '4bp': '4-bet pot',
  'limped pot': 'limped pot',
  
  // ========== TOURNAMENT SYNONYMS ==========
  'icm': 'independent chip model',
  'bubble': 'tournament bubble',
  'on the bubble': 'tournament bubble',
  'ft bubble': 'final table bubble',
  'shortstack': 'short stack',
  'short stacked': 'short stack',
  'bigstack': 'big stack',
  'deep': 'deep stacked',
  'deepstack': 'deep stacked',
  'deep stacked': 'deep stacked',
  'cev': 'chip expected value',
  '$ev': 'dollar expected value',
  'push fold': 'push or fold',
  'push/fold': 'push or fold',
  'shove fold': 'push or fold',
  'nash': 'nash equilibrium',
  
  // ========== COMMON MISSPELLINGS ==========
  'pocker': 'poker',
  'pokker': 'poker',
  'holdem': "hold'em",
  'texas holdem': "texas hold'em",
  'allin': 'all-in',
  'all in': 'all-in',
  'preflop': 'pre-flop',
  'postflop': 'post-flop',
  'villian': 'villain',
  'villan': 'villain',
  'villans': 'villain',
  'opponant': 'opponent',
  'opponnent': 'opponent',
  'agression': 'aggression',
  'bluf': 'bluff',
  'blufing': 'bluffing',
  'equitiy': 'equity',
  'straigh': 'straight',
  'flus': 'flush',
  
  // ========== INFORMAL/SLANG ==========
  'yo': '',
  'hey': '',
  'sup': '',
  'whats': 'what is',
  "what's": 'what is',
  'gimme': 'give me',
  'gonna': 'going to',
  'wanna': 'want to',
  'gotta': 'got to',
  'kinda': 'kind of',
  'sorta': 'sort of',
  'rn': 'right now',
  'tbh': 'to be honest',
  'imo': 'in my opinion',
  'btw': 'by the way',
  'pls': 'please',
  'plz': 'please',
  'thx': 'thanks',
  'ty': 'thank you',
  'np': 'no problem',
  'idk': "I don't know",
  'lmk': 'let me know',
  
  // ========== COMMON QUESTION MAPPINGS ==========
  // Hand Rankings Questions
  'best hand': 'hand_rankings',
  'best hands': 'hand_rankings',
  'best poker hand': 'hand_rankings',
  'best poker hands': 'hand_rankings',
  'poker hands': 'hand_rankings',
  'hand ranking': 'hand_rankings',
  'hand rankings': 'hand_rankings',
  'hands ranking': 'hand_rankings',
  'what beats what': 'hand_rankings',
  'hand order': 'hand_rankings',
  'strongest hand': 'hand_rankings',
  'strongest hands': 'hand_rankings',
  'winning hand': 'hand_rankings',
  'winning hands': 'hand_rankings',
  'hand strength': 'hand_rankings',
  'poker hand order': 'hand_rankings',
  'rank of hands': 'hand_rankings',
  'ranked hands': 'hand_rankings',
  'hand hierarchy': 'hand_rankings',
  'poker hierarchy': 'hand_rankings',
  
  // Best Starting Hands Questions
  'best starting hand': 'best_starting_hands',
  'best starting hands': 'best_starting_hands',
  'best hole cards': 'best_starting_hands',
  'top starting hands': 'best_starting_hands',
  'premium hands': 'best_starting_hands',
  'good starting hands': 'best_starting_hands',
  'what hands to play': 'best_starting_hands',
  'which hands to play': 'best_starting_hands',
  'playable hands': 'best_starting_hands',
  'strong hands': 'best_starting_hands',
  'good hands': 'best_starting_hands',
  
  // Worst Starting Hands Questions  
  'worst hand': 'worst_starting_hands',
  'worst hands': 'worst_starting_hands',
  'worst starting hand': 'worst_starting_hands',
  'worst starting hands': 'worst_starting_hands',
  'bad hands': 'worst_starting_hands',
  'hands to fold': 'worst_starting_hands',
  'trash hands': 'worst_starting_hands',
  'junk hands': 'worst_starting_hands',
  'garbage hands': 'worst_starting_hands',
  
  // Position Questions
  'best position': 'position',
  'worst position': 'position',
  'position order': 'position',
  'seat position': 'position',
  'table position': 'position',
  'positions explained': 'position',
  
  // Basic Rules Questions
  'how to play': 'texas_holdem',
  'how to play poker': 'texas_holdem',
  'poker rules': 'texas_holdem',
  'rules of poker': 'texas_holdem',
  'texas holdem rules': 'texas_holdem',
  'holdem rules': 'texas_holdem',
  'game rules': 'texas_holdem',
  
  // Betting Questions
  'how to bet': 'bet',
  'betting rules': 'bet',
  'bet sizing': 'bet_sizing',
  'how much to bet': 'bet_sizing',
  'proper bet size': 'bet_sizing',
  
  // Blind Questions
  'what are blinds': 'blinds',
  'blind structure': 'blinds',
  'small blind big blind': 'blinds',
  'sb bb': 'blinds',
  
  // Street Questions
  'what is the flop': 'flop',
  'what is the turn': 'turn', 
  'what is the river': 'river',
  'poker streets': 'preflop',
  'betting rounds': 'preflop',
  
  // Pot Odds Questions
  'how to calculate pot odds': 'pot_odds',
  'pot odds explained': 'pot_odds',
  'pot odds calculation': 'pot_odds',
  'calculating pot odds': 'pot_odds',
  
  // Equity Questions
  'how to calculate equity': 'equity',
  'equity explained': 'equity',
  'what is my equity': 'equity',
  'hand equity': 'equity',
  
  // Outs Questions
  'how to count outs': 'outs',
  'counting outs': 'outs',
  'how many outs': 'outs',
  'calculate outs': 'outs',
  
  // ========== HAND NICKNAMES ==========
  'bullets': 'hand_nicknames',
  'pocket rockets': 'hand_nicknames', 
  'american airlines': 'hand_nicknames',
  'cowboys': 'hand_nicknames',
  'king kong': 'hand_nicknames',
  'ladies': 'hand_nicknames',
  'hooks': 'hand_nicknames',
  'fishhooks': 'hand_nicknames',
  'big slick': 'hand_nicknames',
  'anna kournikova': 'hand_nicknames',
  'big chick': 'hand_nicknames',
  'the hammer': 'hand_nicknames',
  'hand nicknames': 'hand_nicknames',
  'poker nicknames': 'hand_nicknames',
  'card nicknames': 'hand_nicknames',
  'what are bullets': 'hand_nicknames',
  'what are cowboys': 'hand_nicknames',
  'what is big slick': 'hand_nicknames',
  
  // ========== STREET STRATEGIES ==========
  'flop strategy': 'flop_strategy',
  'how to play flop': 'flop_strategy',
  'flop play': 'flop_strategy',
  'what to do on flop': 'flop_strategy',
  'playing the flop': 'flop_strategy',
  'turn strategy': 'turn_strategy',
  'how to play turn': 'turn_strategy',
  'turn play': 'turn_strategy',
  'what to do on turn': 'turn_strategy',
  'playing the turn': 'turn_strategy',
  'river strategy': 'river_strategy',
  'how to play river': 'river_strategy',
  'river play': 'river_strategy',
  'what to do on river': 'river_strategy',
  'playing the river': 'river_strategy',
  
  // ========== GAME FORMATS ==========
  'cash game': 'cash_game',
  'cash games': 'cash_game',
  'ring game': 'cash_game',
  'ring games': 'cash_game',
  'tournament': 'tournament',
  'tournaments': 'tournament',
  'tourney': 'tournament',
  'tourneys': 'tournament',
  'mtt': 'tournament',
  'cash vs tournament': 'cash_vs_tournament',
  'cash or tournament': 'cash_vs_tournament',
  'tournament vs cash': 'cash_vs_tournament',
  'cash versus tournament': 'cash_vs_tournament',
  'difference between cash and tournament': 'cash_vs_tournament',
  'heads up': 'heads_up',
  'heads-up': 'heads_up',
  'headsup': 'heads_up',
  'hu poker': 'heads_up',
  '6 max': 'six_max',
  '6max': 'six_max',
  'six max': 'six_max',
  'sixmax': 'six_max',
  'shorthanded': 'six_max',
  'short handed': 'six_max',
  'full ring': 'full_ring',
  'fullring': 'full_ring',
  '9 max': 'full_ring',
  '9max': 'full_ring',
  '10 max': 'full_ring',
  
  // ========== HOW TO BLUFF/READ ==========
  'how to bluff': 'how_to_bluff',
  'how do i bluff': 'how_to_bluff',
  'bluffing strategy': 'how_to_bluff',
  'bluffing tips': 'how_to_bluff',
  'when to bluff': 'how_to_bluff',
  'bluff strategy': 'how_to_bluff',
  'how to read': 'how_to_read_players',
  'how to read players': 'how_to_read_players',
  'reading opponents': 'how_to_read_players',
  'player reads': 'how_to_read_players',
  'tells': 'how_to_read_players',
  'poker tells': 'how_to_read_players',
  'reading tells': 'how_to_read_players',
  
  // ========== BANKROLL ==========
  'bankroll': 'bankroll_management',
  'bankroll management': 'bankroll_management',
  'brm': 'bankroll_management',
  'money management': 'bankroll_management',
  'what stakes': 'bankroll_management',
  'what stakes should i play': 'bankroll_management',
  'how many buy ins': 'bankroll_management',
  'buy ins needed': 'bankroll_management',
  'variance': 'variance',
  'running bad': 'variance',
  'downswing': 'variance',
  'upswing': 'variance',
  'ev': 'expected_value',
  'expected value': 'expected_value',
  '+ev': 'expected_value',
  '-ev': 'expected_value',
  
  // ========== SPECIFIC HAND PLAY ==========
  'how to play aces': 'play_pocket_aces',
  'how to play aa': 'play_pocket_aces',
  'how to play pocket aces': 'play_pocket_aces',
  'playing aces': 'play_pocket_aces',
  'playing aa': 'play_pocket_aces',
  'pocket aces strategy': 'play_pocket_aces',
  'how to play kings': 'play_pocket_kings',
  'how to play kk': 'play_pocket_kings',
  'how to play pocket kings': 'play_pocket_kings',
  'playing kings': 'play_pocket_kings',
  'playing kk': 'play_pocket_kings',
  'pocket kings strategy': 'play_pocket_kings',
  'how to play ak': 'play_ace_king',
  'how to play ace king': 'play_ace_king',
  'playing ak': 'play_ace_king',
  'playing ace king': 'play_ace_king',
  'big slick strategy': 'play_ace_king',
  'how to play small pairs': 'play_small_pairs',
  'how to play pocket pairs': 'play_small_pairs',
  'small pair strategy': 'play_small_pairs',
  'set mining': 'play_small_pairs',
  'how to play suited connectors': 'play_suited_connectors',
  'suited connectors strategy': 'play_suited_connectors',
  'playing suited connectors': 'play_suited_connectors',
  'sc strategy': 'play_suited_connectors',
  
  // ========== HAND COMPARISONS ==========
  'does flush beat straight': 'hand_comparisons',
  'does straight beat flush': 'hand_comparisons',
  'flush vs straight': 'hand_comparisons',
  'straight vs flush': 'hand_comparisons',
  'does full house beat flush': 'hand_comparisons',
  'full house vs flush': 'hand_comparisons',
  'what beats what': 'hand_rankings',
  'what beats a flush': 'hand_comparisons',
  'what beats a straight': 'hand_comparisons',
  'what beats two pair': 'hand_comparisons',
  'what beats three of a kind': 'hand_comparisons',
  'does two pair beat three of a kind': 'hand_comparisons',
  
  // ========== GREETINGS & HELP ==========
  'hello': 'greeting',
  'hi': 'greeting',
  'hey': 'greeting',
  'hi there': 'greeting',
  'hello there': 'greeting',
  'good morning': 'greeting',
  'good afternoon': 'greeting',
  'good evening': 'greeting',
  'whats up': 'greeting',
  "what's up": 'greeting',
  'yo': 'greeting',
  'sup': 'greeting',
  'howdy': 'greeting',
  'help': 'help',
  'help me': 'help',
  'what can you do': 'help',
  'what do you do': 'help',
  'how do you work': 'help',
  'commands': 'help',
  'menu': 'help',
  'options': 'help',
  
  // ========== FRUSTRATED/TILT QUERIES ==========
  'i keep losing': 'variance',
  'why do i keep losing': 'variance',
  'poker is rigged': 'variance',
  'rigged': 'variance',
  'bad beats': 'bad_beat',
  'bad beat': 'bad_beat',
  'i got bad beat': 'bad_beat',
  'so unlucky': 'variance',
  'running bad': 'variance',
  'cooler': 'bad_beat',
  'got coolered': 'bad_beat',
  
  // ========== PLAYING VS PLAYER TYPES ==========
  'how to beat fish': 'beat_fish',
  'beat fish': 'beat_fish',
  'beating fish': 'beat_fish',
  'vs fish': 'beat_fish',
  'against fish': 'beat_fish',
  'play against fish': 'beat_fish',
  'how to beat recreational': 'beat_fish',
  'how to beat bad players': 'beat_fish',
  'how to beat nits': 'beat_nits',
  'beat nits': 'beat_nits',
  'beating nits': 'beat_nits',
  'vs nits': 'beat_nits',
  'against nits': 'beat_nits',
  'how to beat tight players': 'beat_nits',
  'beat tight players': 'beat_nits',
  'how to beat lags': 'beat_lags',
  'beat lags': 'beat_lags',
  'vs lags': 'beat_lags',
  'against lags': 'beat_lags',
  'how to beat aggressive players': 'beat_lags',
  'beat aggressive players': 'beat_lags',
  'how to beat calling stations': 'beat_calling_stations',
  'beat calling stations': 'beat_calling_stations',
  'vs calling stations': 'beat_calling_stations',
  'against calling stations': 'beat_calling_stations',
  'how to beat stations': 'beat_calling_stations',
  
  // ========== STAT QUESTIONS ==========
  'is my vpip good': 'good_vpip',
  'good vpip': 'good_vpip',
  'optimal vpip': 'good_vpip',
  'what vpip should i have': 'good_vpip',
  'is 25 vpip good': 'good_vpip',
  'is 30 vpip good': 'good_vpip',
  'is 20 vpip good': 'good_vpip',
  'vpip too high': 'good_vpip',
  'vpip too low': 'good_vpip',
  'is my pfr good': 'good_pfr',
  'good pfr': 'good_pfr',
  'optimal pfr': 'good_pfr',
  'what pfr should i have': 'good_pfr',
  'pfr too high': 'good_pfr',
  'pfr too low': 'good_pfr',
  'is my 3bet good': 'good_3bet',
  'good 3bet': 'good_3bet',
  'optimal 3bet': 'good_3bet',
  '3bet percentage': 'good_3bet',
  'how often should i 3bet': 'good_3bet',
  
  // ========== FOLLOW-UP REQUESTS ==========
  'tell me more': 'tell_me_more',
  'explain more': 'tell_me_more',
  'more info': 'tell_me_more',
  'more information': 'tell_me_more',
  'more details': 'tell_me_more',
  'elaborate': 'tell_me_more',
  'go deeper': 'tell_me_more',
  'what else': 'tell_me_more',
  'anything else': 'tell_me_more',
  'continue': 'tell_me_more',
  'keep going': 'tell_me_more',
  'and': 'tell_me_more',
  'more examples': 'tell_me_more',
  'give me examples': 'tell_me_more',
  'show me examples': 'tell_me_more',
  
  // ========== SIMPLE TERMS REQUEST ==========
  'in simple terms': 'greeting',
  'simpler': 'greeting',
  'explain like im 5': 'greeting',
  'eli5': 'greeting',
  'dumb it down': 'greeting',
  'too complicated': 'greeting',
};

// ============================================================================
// PLATFORM KNOWLEDGE - PokerSweepStars Specific Info
// ============================================================================

export const PLATFORM_KNOWLEDGE: Record<string, {
  title: string;
  description: string;
  details?: string[];
  tips?: string[];
  relatedTopics?: string[];
}> = {
  // ========== CURRENCIES ==========
  pkcash: {
    title: 'PSS Coins - Redeemable Currency',
    description: 'PSS Coins are PokerSweepStars premium currency that can be redeemed for prizes.',
    details: [
      'PSS Coins can be earned or earned through play',
      'Withdrawable after meeting playthrough requirements',
      'Used at PSS Coins tables (prize-eligible games)',
      'Can be earned through training, challenges, and referrals',
    ],
    tips: [
      'Complete daily training for free PSS Coins',
      'Play within your bankroll - 20+ buy-ins recommended',
      'KYC verification required for withdrawals',
    ],
    relatedTopics: ['playcoins', 'withdrawal', 'playthrough'],
  },
  playcoins: {
    title: 'PlayCoins - Free Practice Currency',
    description: 'PlayCoins are free-to-play chips for practicing poker without any risk.',
    details: [
      'Cannot be withdrawn or converted to PSS Coins',
      'Perfect for learning the game',
      'Unlimited free refills available',
      'No playthrough requirements',
    ],
    tips: [
      'New to poker? Start with PlayCoins!',
      'Practice new strategies risk-free',
      'Great for testing unconventional plays',
    ],
    relatedTopics: ['pkcash', 'training_mode'],
  },
  playthrough: {
    title: 'Playthrough Requirements',
    description: 'The amount you need to wager before withdrawing bonus funds.',
    details: [
      'Typical playthrough: 1x-5x the bonus amount',
      'Example: $10 bonus with 5x playthrough = play $50 in hands',
      'Only raked hands count toward playthrough',
      'Check your progress in wallet section',
    ],
    tips: [
      'Play your normal game - don\'t chase playthrough',
      'Micro stakes count toward playthrough too',
      'Playthrough completes automatically over time',
    ],
    relatedTopics: ['pkcash', 'withdrawal', 'bonus'],
  },
  
  // ========== FEATURES ==========
  training_mode: {
    title: 'Training Mode - Learn & Earn',
    description: 'Practice poker scenarios and earn PSS Coins rewards while improving your game!',
    details: [
      '100+ training scenarios across all skill levels',
      'Earn PSS Coins for correct answers',
      'Daily challenges with bonus rewards',
      'Streak bonuses up to 2x rewards',
      'Covers preflop, postflop, and advanced concepts',
    ],
    tips: [
      'Complete 5 daily challenges for bonus PSS Coins',
      'Focus on your weakest concepts',
      'Maintain streaks for multiplied rewards',
    ],
    relatedTopics: ['daily_challenge', 'training_streak', 'scenarios'],
  },
  daily_challenge: {
    title: 'Daily Training Challenge',
    description: 'Complete 5 scenarios daily for bonus PSS Coins rewards!',
    details: [
      '5 new scenarios every day',
      '$0.15 PSS Coins for completing all 5',
      '$0.25 bonus for getting all 5 correct',
      'Resets at midnight UTC',
    ],
    tips: [
      'Do challenges before playing to warm up',
      'Build streaks for multiplied rewards',
      'Scenarios adapt to your skill level',
    ],
    relatedTopics: ['training_mode', 'training_streak'],
  },
  training_streak: {
    title: 'Training Streak Bonuses',
    description: 'Train consecutive days for multiplied PSS Coins rewards!',
    details: [
      '3-day streak: 1.25x rewards',
      '7-day streak: 1.5x rewards',
      '14-day streak: 1.75x rewards',
      '30-day streak: 2x rewards',
    ],
    tips: [
      'Just complete one scenario to maintain streak',
      'Streaks reset at midnight UTC',
      'Weekend training counts too!',
    ],
    relatedTopics: ['training_mode', 'daily_challenge'],
  },
  challenges: {
    title: 'Monthly Challenges',
    description: 'Compete in monthly challenges for prizes and leaderboard glory!',
    details: [
      'New challenge themes each month',
      'Examples: Hands Champion, Biggest Pot, Longest Streak',
      'PSS Coins prizes for top finishers',
      'Separate leaderboards per challenge',
    ],
    tips: [
      'Check active challenges in the Challenges tab',
      'Some challenges reward consistency over volume',
    ],
    relatedTopics: ['leaderboard', 'pkcash'],
  },
  leaderboard: {
    title: 'Leaderboards',
    description: 'Compete against other players for weekly and monthly prizes!',
    details: [
      'Weekly hands won leaderboard',
      'Monthly PSS Coins prizes',
      'Training leaderboard with separate prizes',
      'All-time rankings for bragging rights',
    ],
    tips: [
      'Weekly leaderboard resets Sunday midnight UTC',
      'Top 10 get PSS Coins prizes',
    ],
    relatedTopics: ['challenges', 'training_mode'],
  },
  referral: {
    title: 'Referral Program',
    description: 'Invite friends and earn commission on their play!',
    details: [
      'Share your unique referral link',
      'Earn % of rake from referred players',
      'Become an Agent for higher commissions',
      'Commissions paid automatically',
    ],
    tips: [
      'Share on social media for more referrals',
      'Agents earn 20-30% commission',
    ],
    relatedTopics: ['agent', 'pkcash'],
  },
  agent: {
    title: 'Agent Program',
    description: 'Become an agent to earn higher referral commissions!',
    details: [
      'Higher commission rates (20-30%)',
      'Dashboard to track your players',
      'Monthly commission payouts',
      'Requirements: Active account, verified identity',
    ],
    tips: [
      'Contact support to apply for agent status',
      'Build a player network for passive income',
    ],
    relatedTopics: ['referral', 'pkcash'],
  },
  
  // ========== ACCOUNT ==========
  kyc: {
    title: 'KYC Verification',
    description: 'Know Your Customer verification required for withdrawals.',
    details: [
      'Upload government-issued ID',
      'Take a selfie for verification',
      'Processing typically 24-48 hours',
      'Required for all withdrawals',
    ],
    tips: [
      'Use clear, well-lit photos',
      'Ensure ID is not expired',
      'Selfie must match ID photo',
    ],
    relatedTopics: ['withdrawal', 'account'],
  },
  withdrawal: {
    title: 'Redeeming PSS Coins',
    description: 'How to redeem your PSS Coins winnings.',
    details: [
      'Complete KYC verification first',
      'Meet any playthrough requirements',
      'Minimum withdrawal: Check current limits',
      'Processing time: 1-3 business days',
    ],
    tips: [
      'Keep some bankroll for future play',
      'Withdrawals processed Monday-Friday',
    ],
    relatedTopics: ['kyc', 'pkcash', 'playthrough'],
  },
  deposit: {
    title: 'Adding Funds',
    description: 'Get PSS Coins to play at PSS Coins tables.',
    details: [
      'Multiple payment methods available',
      'Instant credit to your account',
      'Deposit bonuses may be available',
    ],
    tips: [
      'Only deposit what you can afford to lose',
      'Check for active deposit bonuses',
    ],
    relatedTopics: ['pkcash', 'bonus'],
  },
  banned: {
    title: 'Account Bans',
    description: 'Information about account restrictions.',
    details: [
      'Bans may be temporary or permanent',
      'Common reasons: multi-accounting, collusion, fraud',
      'Appeal process available',
      'Funds may be frozen during investigation',
    ],
    tips: [
      'Play fair and follow terms of service',
      'Only use one account per person',
      'Contact support to appeal if you believe ban is in error',
    ],
    relatedTopics: ['appeal', 'support'],
  },
  appeal: {
    title: 'Appealing a Ban',
    description: 'How to appeal an account restriction.',
    details: [
      'Submit appeal through banned page',
      'Provide relevant information/explanation',
      'Review typically takes 3-7 business days',
      'Decision is communicated via email',
    ],
    tips: [
      'Be honest in your appeal',
      'Provide any evidence supporting your case',
    ],
    relatedTopics: ['banned', 'support'],
  },
  
  // ========== GAMEPLAY ==========
  cash_games: {
    title: 'Cash Games',
    description: 'Ring games where you can buy in and cash out anytime.',
    details: [
      'PSS Coins tables: Prize-eligible play',
      'PlayCoins tables: Free practice',
      'Various stakes available',
      'No fixed end time',
    ],
    tips: [
      'Start at lower stakes to learn',
      'Table selection matters - pick soft tables',
      '20+ buy-ins recommended for your stake',
    ],
    relatedTopics: ['pkcash', 'playcoins', 'bankroll'],
  },
  tournaments: {
    title: 'Tournament Poker',
    description: 'Multi-table tournaments with fixed buy-ins and prize pools.',
    details: [
      'Various formats: Freezeout, Rebuy, Turbo',
      'Prize pool distributed to top finishers',
      'Blind levels increase over time',
      'Late registration available on most',
    ],
    tips: [
      'Adjust strategy for tournament play',
      'ICM matters near the bubble and final table',
      'Manage your stack relative to blinds',
    ],
    relatedTopics: ['icm', 'bubble', 'pkcash'],
  },
  private_tables: {
    title: 'Private Tables',
    description: 'Create private games for you and your friends!',
    details: [
      'Set your own blind levels',
      'Invite-only games',
      'Both PSS Coins and PlayCoins options',
      'Perfect for home game style play',
    ],
    tips: [
      'Share game code with friends to invite them',
      'Set appropriate stakes for your group',
    ],
    relatedTopics: ['friends', 'cash_games'],
  },
};

export interface ParsedQuery {
  type: QueryType;
  originalQuery: string;
  extractedEntities: {
    term?: string;
    handNumber?: number;
    action?: string;
    hand?: string;
    position?: string;
    concept?: ScenarioConcept;
    difficulty?: ScenarioDifficulty;
    potSize?: number;
    betSize?: number;
    callAmount?: number;
    outs?: number;
    statName?: string;
    stackSize?: number;  // For short stack queries
  };
  confidence: number;
}

export interface CoachResponse {
  type: 'text' | 'calculation' | 'scenario' | 'hand_review' | 'glossary' | 'analysis' | 'plan';
  title?: string;
  content: string;
  details?: string;
  mathBreakdown?: string;
  scenario?: Scenario;
  relatedTerms?: string[];
  followUpQuestions?: string[];
  leaks?: Leak[];
  improvementPlan?: ImprovementPlan;
  priority?: 'critical' | 'high' | 'medium' | 'low';
}

export interface ConversationMessage {
  role: 'user' | 'coach';
  content: string;
  timestamp: number;
  response?: CoachResponse;
}

// ============================================================================
// ENHANCED QUERY PATTERNS
// ============================================================================

const QUERY_PATTERNS: Record<QueryType, RegExp[]> = {
  definition: [
    /what (?:is|are|does)(?: an?| the)? (\w+(?:\s+\w+)?)/i,
    /explain (?:what )?([\w\s]+)/i,
    /define (?:the )?([\w\s]+)/i,
    /meaning of ([\w\s]+)/i,
    /what(?:'s| is) (vpip|pfr|c-?bet|pot odds|equity|outs|implied odds|mdf|gto|range|position|aggression|wtsd|w\$sd|wwsf|3-?bet|steal|af)/i,
    /tell me about ([\w\s-]+)/i,
  ],
  hand_review: [
    /(?:review|analyze|explain) hand #?(\d+)/i,
    /what (?:went wrong|happened)(?: in)? hand #?(\d+)/i,
    /why was (?:my )?(fold|call|raise|bet|check) (?:bad|wrong|mistake|good)/i,
    /hand (?:number )?#?(\d+)/i,
    /look at hand #?(\d+)/i,
    /biggest mistake/i,
    /worst hand/i,
    /my mistakes/i,
  ],
  should_i: [
    /should i (fold|call|raise|bet|check)/i,
    /is (?:this|that|it) a (fold|call|raise)/i,
    /(?:should|would) (?:you|i) (fold|call|raise|bet)/i,
    /what should i do/i,
    /is ([\w\d]+) a (fold|call|raise)/i,
  ],
  scenario_request: [
    /(?:give|create|show|get)(?: me)? (?:a|an|some) ([\w\s-]+) (?:scenario|spot|situation|hand|example)/i,
    /practice ([\w\s-]+)/i,
    /drill (?:me on )?([\w\s-]+)/i,
    /(?:let's|lets) practice ([\w\s-]+)/i,
    /(?:scenario|spot|situation) (?:for|about|on) ([\w\s-]+)/i,
    /random scenario/i,
    /give me a hand to practice/i,
    /practice my weak(?:est)? area/i,
  ],
  range_question: [
    /what (?:hands|cards) (?:should i|to|can i) (?:play|open|raise)(?: from| in)? ([\w+-]+)/i,
    /([\w+-]+) (?:range|hands|opening)/i,
    /what(?:'s| is) (?:the )?([\w+-]+) range/i,
    /hands (?:to play )?from ([\w+-]+)/i,
    /(?:opening|raise|play) range (?:for|from|in) ([\w+-]+)/i,
  ],
  odds_calculation: [
    /pot odds/i,
    /what(?:'s| are)(?: the)? (?:pot )?odds/i,
    /pot (?:is )?\$?(\d+).*(?:bet|call|raise)(?: is| of)? \$?(\d+)/i,
    /\$?(\d+) (?:in|into)(?: the)? pot.*\$?(\d+) (?:to call|bet)/i,
    /odds (?:of|to|for) call/i,
  ],
  equity_calculation: [
    /(?:what(?:'s| is))?(?: my)? equity/i,
    /(\d+) outs/i,
    /(flush draw|straight draw|oesd|gutshot|combo draw|backdoor)/i,
    /chance(?:s)? (?:of|to) (?:hit|make|improve)/i,
    /how likely (?:am i |to )?(?:hit|make)/i,
    /([\w\d]+) (?:vs|versus|against) ([\w\d]+)/i,
  ],
  session_review: [
    /(?:review|analyze|look at|summarize)(?: my)? (?:session|report|play|performance)/i,
    /how did i (?:do|play|perform)/i,
    /(?:my )?(?:biggest )?(?:mistakes?|leaks?|weaknesses?)/i,
    /what (?:should i|to|do i need to) (?:work on|improve|focus)/i,
    /(?:summarize|summary)(?: my)? (?:session|play)/i,
    /where (?:do i|should i) improve/i,
    /based on (?:my )?(?:latest|last|recent) (?:session|report|play)/i,
    /my stats/i,
    /how(?:'s| is) my (?:game|play|performance)/i,
  ],
  stat_analysis: [
    /(?:analyze|check|what(?:'s| is))(?: my)? (vpip|pfr|c-?bet|aggression|wtsd|af)/i,
    /is (?:my )?(vpip|pfr|c-?bet|aggression|wtsd|af) (?:good|bad|ok|optimal)/i,
    /(?:optimal|ideal|best|good) (vpip|pfr|c-?bet|aggression|af|wtsd)/i,
    /what should (?:my )?(vpip|pfr|c-?bet|aggression|af) be/i,
  ],
  leak_detection: [
    /(?:find|detect|what(?:'s| are))(?: my)? leaks?/i,
    /what(?:'s| is) (?:wrong with|the problem with)(?: my)? (?:game|play)/i,
    /why am i losing/i,
    /where am i (?:losing|bleeding) money/i,
    /what(?:'s| is) hurting my (?:game|winrate)/i,
  ],
  improvement_plan: [
    /(?:create|make|give me)(?: an?)? (?:improvement|training|study) plan/i,
    /how (?:do i|can i|to) (?:get better|improve)/i,
    /what should i (?:study|learn|work on|focus on)/i,
    /help me improve/i,
    /next steps/i,
    /weekly goals/i,
  ],
  position_advice: [
    /(?:how to |should i )?play (?:from |in )?(utg|utg\+1|mp|co|cutoff|btn|button|sb|bb)/i,
    /position (?:advice|tips|strategy)/i,
    /(utg|mp|co|btn|sb|bb) (?:strategy|play|tips)/i,
    /playing (?:in|out of) position/i,
  ],
  opponent_analysis: [
    /(?:how to |should i )?play (?:against|vs) (?:a )?(nit|tag|lag|fish|maniac|calling station|whale|rock)/i,
    /(?:exploit|beat) (?:a )?(nit|tag|lag|fish|maniac|calling station)/i,
    /opponent (?:has|with|is) (?:vpip|pfr) (?:of )?(\d+)/i,
  ],
  bet_sizing: [
    /how (?:much|big) (?:should i|to) (?:bet|raise)/i,
    /(?:bet|raise) (?:size|sizing)/i,
    /what(?:'s| is) (?:the )?(correct|optimal|best) (?:bet|raise) size/i,
    /(\d+)% pot/i,
  ],
  general_advice: [
    /(?:how (?:do|should|can) i|tips? (?:for|on|about)|advice (?:for|on|about)) ([\w\s]+)/i,
    /(?:help|teach)(?: me)? (?:with |about |to )?([\w\s]+)/i,
    /(?:getting better|improve)(?: at)? ([\w\s]+)/i,
    /beginner (?:tips|advice|guide)/i,
    /how to (?:play|win|improve)/i,
  ],
  // NEW: Platform-specific patterns
  platform_help: [
    /(?:how (?:do|can) i |what(?:'s| is) )?(pkcash|playcoins?|withdrawal?|deposit|kyc|verify|verification)/i,
    /(?:how to|can i) (?:get|earn|win|withdraw|deposit|add) (?:pkcash|playcoins?|cash|money|funds)/i,
    /(?:what(?:'s| is)|how does|explain) (?:the )?(playthrough|rake|agent|referral)/i,
    /(?:help with|about) (?:my )?(?:account|wallet|balance|withdrawal|deposit)/i,
    /(?:how to|can i) (?:become|be) (?:an )?agent/i,
    /(?:private|create|start) (?:table|game|room)/i,
    /(?:what(?:'s| are)|how do) (?:the )?(challenges?|leaderboard|tournaments?)/i,
    /(?:why|how) (?:was i |am i |did i get )?(banned|restricted|suspended)/i,
    /(?:how to|can i) appeal/i,
    /how (?:does|do) (?:the )?(app|platform|pokersweepstars|site) work/i,
  ],
  training_rewards: [
    /(?:how (?:do|can) i |what(?:'s| are) )?(training|scenario) rewards?/i,
    /(?:earn|get|win) (?:pkcash )?(from|through|with|by) training/i,
    /training (?:streak|bonus|multiplier)/i,
    /(?:what(?:'s| is)|how does) (?:the )?streak (?:work|bonus)/i,
    /(?:how much|what) (?:do i|can i) (?:earn|get|win) (?:from |per )?(?:training|scenario)/i,
    /mastery (?:bonus|reward)/i,
    /concept (?:mastery|badge)/i,
  ],
  daily_challenge: [
    /daily (?:challenge|training|scenarios?)/i,
    /(?:today(?:'s)?|todays) (?:challenge|training|scenarios?)/i,
    /(?:what(?:'s| is)|show)(?: me)?(?: the)? daily (?:challenge)?/i,
    /(?:start|do|begin|play)(?: the)? daily/i,
    /(?:daily |today(?:'s)? )?challenge (?:reward|bonus)/i,
  ],
  short_stack: [
    /(?:short stack|shortstack|short-stack) (?:play|strategy|tips)/i,
    /(\d+) ?(?:bb|big blinds?) (?:push|shove|play|strategy)/i,
    /push(?:\/| )?fold (?:chart|strategy|range)/i,
    /(?:when to|should i) (?:shove|push|jam) (\d+) ?bb/i,
    /(?:what(?:'s| is)|show)(?: me)?(?: the)? shove range/i,
    /short stacked in tournament/i,
  ],
  tournament_icm: [
    /icm (?:pressure|spot|decision|strategy)/i,
    /(?:bubble|final table) (?:play|strategy|decision)/i,
    /(?:should i|how to) (?:play|fold|call) (?:on|at) (?:the )?bubble/i,
    /tournament (?:strategy|tips|advice)/i,
    /(?:pay jump|payout) (?:decision|consideration)/i,
    /(?:chip|stack) (?:pressure|icm)/i,
    /satellite (?:bubble|strategy)/i,
  ],
  // NEW: Player profile / player type questions
  player_profile: [
    // Self-referential questions about being called a type
    /what (?:do you mean|does it mean|you mean|does that mean)(?: i(?:'m| am))? (?:a |an )?(fish|nit|tag|lag|rock|maniac|calling station|shark|whale|donkey|reg|recreational)/i,
    /why (?:did you|do you|would you) (?:call|say|label) (?:me|i(?:'m| am)|my (?:profile|play|style))(?: a| an)? (fish|nit|tag|lag|rock|maniac|calling station|shark|whale|donkey|reg|recreational)/i,
    /(?:you said|you called|it says|profile (?:is|says)|my profile is)(?: i(?:'m| am))?(?: a| an)? (fish|nit|tag|lag|rock|maniac|calling station|shark|whale|donkey|reg|recreational)/i,
    /what (?:is|does)(?: a| an)? (fish|nit|tag|lag|rock|maniac|calling station|shark|whale|donkey|reg|recreational)(?: mean| player| style)?/i,
    /(?:am i|i(?:'m| am))(?: really)?(?: a| an)? (fish|nit|tag|lag|rock|maniac|calling station|shark|whale|donkey|reg|recreational)\??/i,
    /(?:what(?:'s| is)|explain)(?: a| an| the)? (fish|nit|tag|lag|rock|maniac|calling station|shark|whale|donkey|reg|recreational)(?: player| type| style)?/i,
    /(?:how|why) (?:am i|is my (?:profile|play|style))(?: classified| labeled| categorized| called)?(?: as)?(?: a| an)? (fish|nit|tag|lag|rock|maniac|calling station|shark|whale|donkey|reg|recreational)?/i,
    /player (?:type|profile|style|classification)/i,
    /what(?:'s| is) my player (?:type|profile|style)/i,
    /(?:define|explain) (?:the )?player (?:types?|profiles?|styles?)/i,
    /what (?:are|is) (?:the )?(?:different )?(player (?:types?|profiles?|styles?)|playing styles?)/i,
  ],
  unknown: [],
};

// ============================================================================
// QUERY PARSER
// ============================================================================

/**
 * Apply synonyms to normalize user query
 */
function applySynonyms(query: string): string {
  let normalized = query.toLowerCase().trim();
  
  // Sort synonyms by length (longest first) to avoid partial replacements
  const sortedSynonyms = Object.entries(QUERY_SYNONYMS)
    .sort((a, b) => b[0].length - a[0].length);
  
  for (const [slang, standard] of sortedSynonyms) {
    // Use word boundaries to avoid partial matches
    const regex = new RegExp(`\\b${slang.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    normalized = normalized.replace(regex, standard);
  }
  
  return normalized;
}

/**
 * Check if query is asking about platform features
 */
function checkPlatformQuery(query: string): string | null {
  const platformTerms = Object.keys(PLATFORM_KNOWLEDGE);
  const normalizedQuery = query.toLowerCase();
  
  for (const term of platformTerms) {
    // Check for direct mention or common variations
    const termVariations = [
      term,
      term.replace(/_/g, ' '),
      term.replace(/_/g, '-'),
    ];
    
    for (const variation of termVariations) {
      if (normalizedQuery.includes(variation)) {
        return term;
      }
    }
  }
  
  return null;
}

export function parseQuery(query: string): ParsedQuery {
  // First apply synonyms to normalize the query
  const normalizedQuery = applySynonyms(query);
  
  // Check for platform-specific queries first
  const platformTerm = checkPlatformQuery(normalizedQuery);
  if (platformTerm) {
    return {
      type: 'platform_help',
      originalQuery: query,
      extractedEntities: { term: platformTerm },
      confidence: 0.9,
    };
  }
  
  for (const [type, patterns] of Object.entries(QUERY_PATTERNS)) {
    if (type === 'unknown') continue;
    
    for (const pattern of patterns) {
      const match = normalizedQuery.match(pattern);
      if (match) {
        const parsed: ParsedQuery = {
          type: type as QueryType,
          originalQuery: query,
          extractedEntities: {},
          confidence: 0.8,
        };
        
        // Extract entities based on query type
        switch (type) {
          case 'definition':
            parsed.extractedEntities.term = match[1]?.trim();
            break;
          case 'hand_review':
            const handNum = match[1] || query.match(/\d+/)?.[0];
            if (handNum) parsed.extractedEntities.handNumber = parseInt(handNum);
            break;
          case 'should_i':
            parsed.extractedEntities.action = match[1]?.toLowerCase();
            break;
          case 'scenario_request':
            const conceptText = match[1]?.toLowerCase().trim();
            parsed.extractedEntities.concept = mapToConcept(conceptText);
            parsed.extractedEntities.difficulty = extractDifficulty(query);
            break;
          case 'range_question':
            parsed.extractedEntities.position = normalizePosition(match[1]);
            break;
          case 'odds_calculation':
            const nums = query.match(/\$?(\d+)/g);
            if (nums && nums.length >= 2) {
              parsed.extractedEntities.potSize = parseInt(nums[0].replace('$', ''));
              parsed.extractedEntities.betSize = parseInt(nums[1].replace('$', ''));
            }
            break;
          case 'equity_calculation':
            const outsMatch = query.match(/(\d+)\s*outs?/i);
            if (outsMatch) {
              parsed.extractedEntities.outs = parseInt(outsMatch[1]);
            }
            // Check for draw types
            for (const [drawType, outs] of Object.entries(DRAW_OUTS)) {
              if (normalizedQuery.includes(drawType.replace('-', ' ')) || 
                  normalizedQuery.includes(drawType)) {
                parsed.extractedEntities.outs = outs;
                break;
              }
            }
            break;
          case 'stat_analysis':
            parsed.extractedEntities.statName = match[1]?.toLowerCase();
            break;
          case 'position_advice':
            parsed.extractedEntities.position = normalizePosition(match[1]);
            break;
          case 'short_stack':
            const bbMatch = normalizedQuery.match(/(\d+)\s*(?:bb|big blinds?)/i);
            if (bbMatch) {
              parsed.extractedEntities.stackSize = parseInt(bbMatch[1]);
            }
            break;
          case 'platform_help':
            parsed.extractedEntities.term = match[1]?.toLowerCase();
            break;
          case 'player_profile':
            // Extract the player type from the match or query
            const playerTypeMatch = normalizedQuery.match(/\b(fish|nit|tag|lag|rock|maniac|calling station|shark|whale|donkey|reg|recreational)\b/i);
            if (playerTypeMatch) {
              parsed.extractedEntities.playerType = playerTypeMatch[1].toLowerCase();
            } else if (match[1]) {
              parsed.extractedEntities.playerType = match[1].toLowerCase();
            }
            break;
        }
        
        return parsed;
      }
    }
  }
  
  // If no pattern matched, try to guess based on keywords
  return {
    type: guessQueryType(normalizedQuery),
    originalQuery: query,
    extractedEntities: {},
    confidence: 0.3,
  };
}

function normalizePosition(pos: string | undefined): string {
  if (!pos) return '';
  const normalized = pos.toUpperCase().replace(/\+/g, '+1');
  const aliases: Record<string, string> = {
    'CUTOFF': 'CO',
    'BUTTON': 'BTN',
    'SMALL BLIND': 'SB',
    'BIG BLIND': 'BB',
    'UNDER THE GUN': 'UTG',
  };
  return aliases[normalized] || normalized;
}

function mapToConcept(text: string | undefined): ScenarioConcept | undefined {
  if (!text) return undefined;
  
  const conceptMap: Record<string, ScenarioConcept> = {
    'preflop': 'preflop-ranges',
    'range': 'preflop-ranges',
    'ranges': 'preflop-ranges',
    'starting hands': 'preflop-ranges',
    'position': 'position',
    'positional': 'position',
    'pot odds': 'pot-odds',
    'odds': 'pot-odds',
    'implied odds': 'implied-odds',
    'value': 'value-betting',
    'value bet': 'value-betting',
    'value betting': 'value-betting',
    'bluff': 'bluffing',
    'bluffing': 'bluffing',
    'semi bluff': 'semi-bluffing',
    'semi-bluff': 'semi-bluffing',
    'semibluff': 'semi-bluffing',
    'bet sizing': 'bet-sizing',
    'sizing': 'bet-sizing',
    'c-bet': 'continuation-bet',
    'cbet': 'continuation-bet',
    'continuation bet': 'continuation-bet',
    'check raise': 'check-raise',
    'check-raise': 'check-raise',
    'pot control': 'pot-control',
    'thin value': 'thin-value',
    'river': 'river-decisions',
    'draw': 'draws',
    'draws': 'draws',
    'board texture': 'board-texture',
    'weak': 'preflop-ranges', // For "practice my weak area"
    'weakest': 'preflop-ranges',
  };
  
  for (const [key, concept] of Object.entries(conceptMap)) {
    if (text.includes(key)) return concept;
  }
  
  return undefined;
}

function extractDifficulty(query: string): ScenarioDifficulty | undefined {
  const lower = query.toLowerCase();
  if (lower.includes('beginner') || lower.includes('easy') || lower.includes('basic')) return 'beginner';
  if (lower.includes('advanced') || lower.includes('hard') || lower.includes('difficult')) return 'advanced';
  if (lower.includes('intermediate') || lower.includes('medium')) return 'intermediate';
  return undefined;
}

function guessQueryType(query: string): QueryType {
  // Platform-related keywords
  if (query.includes('pkcash') || query.includes('playcoins') || query.includes('withdraw') || 
      query.includes('deposit') || query.includes('kyc') || query.includes('verify') ||
      query.includes('agent') || query.includes('referral') || query.includes('banned') ||
      query.includes('appeal') || query.includes('playthrough')) return 'platform_help';
  
  // Training-related keywords
  if (query.includes('daily') && (query.includes('challenge') || query.includes('training'))) return 'daily_challenge';
  if (query.includes('streak') || query.includes('training reward') || query.includes('earn from training')) return 'training_rewards';
  
  // Tournament/ICM keywords
  if (query.includes('icm') || query.includes('bubble') || query.includes('final table') ||
      query.includes('tournament') || query.includes('satellite')) return 'tournament_icm';
  
  // Short stack keywords
  if (query.includes('short stack') || query.includes('push fold') || query.includes('shove range') ||
      query.match(/\d+\s*bb/)) return 'short_stack';
  
  // Player profile keywords - check BEFORE general definition
  const playerTypeWords = ['fish', 'nit', 'tag', 'lag', 'rock', 'maniac', 'calling station', 'shark', 'whale', 'donkey', 'reg', 'recreational', 'player type', 'player profile', 'playing style'];
  if (playerTypeWords.some(word => query.includes(word))) return 'player_profile';
  
  // Self-referential questions about profile
  if ((query.includes('why') || query.includes('what')) && 
      (query.includes('am i') || query.includes("i'm") || query.includes('im a') || query.includes('my profile') || query.includes('you said') || query.includes('you call'))) return 'player_profile';
  
  // Challenge coaching: "How do I beat Blind Assassin?" and friends. Player
  // types ("beat fish") are already routed above.
  if (/\b(beat|win|crush)\b/.test(query) &&
      /(drill|challenge|assassin|double.?up|deep water|comeback|ice veins|giant slayer|surgeon|lazarus|mission|this run)/.test(query)) {
    return 'challenge_help';
  }
  if (query.includes('work on') || query.includes('next step') || query.includes('get better') || query.includes('improvement plan')) {
    return 'improvement_plan';
  }

  // Standard keywords
  if (query.includes('what is') || query.includes('what does') || query.includes('mean')) return 'definition';
  if (query.includes('hand') && query.match(/\d/)) return 'hand_review';
  if (query.includes('should') || query.includes('fold') || query.includes('call')) return 'should_i';
  if (query.includes('scenario') || query.includes('practice') || query.includes('drill')) return 'scenario_request';
  if (query.includes('range') || query.includes('hands from')) return 'range_question';
  if (query.includes('pot') && query.includes('odds')) return 'odds_calculation';
  if (query.includes('equity') || query.includes('outs') || query.includes('chance')) return 'equity_calculation';
  if (query.includes('session') || query.includes('review') || query.includes('mistake') || query.includes('improve')) return 'session_review';
  if (query.includes('leak') || query.includes('losing')) return 'leak_detection';
  if (query.includes('vpip') || query.includes('pfr') || query.includes('stat')) return 'stat_analysis';
  return 'general_advice';
}

// ============================================================================
// RESPONSE GENERATORS
// ============================================================================

export function generateResponse(
  parsed: ParsedQuery,
  context?: {
    sessionReport?: TrainingReport;
    currentHand?: LoggedDecision;
    recentDecisions?: LoggedDecision[];
    latestReport?: TrainingReport;
  }
): CoachResponse {
  // Use latest report if session report not provided
  const report = context?.sessionReport || context?.latestReport;
  
  switch (parsed.type) {
    case 'definition':
      return generateDefinitionResponse(parsed.extractedEntities.term);
    
    case 'hand_review':
      return generateHandReviewResponse(
        parsed.extractedEntities.handNumber,
        context?.recentDecisions,
        report
      );
    
    case 'should_i':
      return generateShouldIResponse(parsed, context?.currentHand);
    
    case 'scenario_request':
      return generateScenarioResponse(
        parsed.extractedEntities.concept,
        parsed.extractedEntities.difficulty,
        report
      );
    
    case 'range_question':
      return generateRangeResponse(parsed.extractedEntities.position);
    
    case 'odds_calculation':
      return generateOddsResponse(
        parsed.extractedEntities.potSize,
        parsed.extractedEntities.betSize
      );
    
    case 'equity_calculation':
      return generateEquityResponse(
        parsed.extractedEntities.outs,
        parsed.extractedEntities.hand,
        parsed.extractedEntities.term
      );
    
    case 'session_review':
      return generateSessionReviewResponse(report);

    case 'challenge_help':
      return generateChallengeHelpResponse(report);
    
    case 'stat_analysis':
      return generateStatAnalysisResponse(parsed.extractedEntities.statName, report);
    
    case 'leak_detection':
      return generateLeakDetectionResponse(report);
    
    case 'improvement_plan':
      return generateImprovementPlanResponse(report);
    
    case 'position_advice':
      return generatePositionAdviceResponse(parsed.extractedEntities.position);
    
    case 'opponent_analysis':
      return generateOpponentAnalysisResponse(parsed.originalQuery);
    
    case 'bet_sizing':
      return generateBetSizingResponse(parsed.originalQuery);
    
    case 'general_advice':
      return generateGeneralAdviceResponse(parsed.originalQuery);
    
    // NEW: Platform-specific responses
    case 'platform_help':
      return generatePlatformHelpResponse(parsed.extractedEntities.term);
    
    case 'training_rewards':
      return generateTrainingRewardsResponse();
    
    case 'daily_challenge':
      return generateDailyChallengeResponse();
    
    case 'short_stack':
      return generateShortStackResponse(parsed.extractedEntities.stackSize);
    
    case 'tournament_icm':
      return generateTournamentICMResponse(parsed.originalQuery);
    
    case 'player_profile':
      return generatePlayerProfileResponse(
        parsed.extractedEntities.playerType,
        report
      );
    
    default:
      return generateUnknownResponse(parsed.originalQuery);
  }
}

// ============================================================================
// CHALLENGE HELP - "How do I beat X?" answered with the playbook AND the
// player's own gaps from their last run.
// ============================================================================

function generateChallengeHelpResponse(report?: TrainingReport): CoachResponse {
  const challenge: any = (report as any)?.challenge;
  if (!challenge) {
    return {
      type: 'text',
      title: 'Challenge Strategy',
      content: 'Open a challenge session first (play one, then come here from its training report) and I can coach you on beating that exact challenge with your own hands as evidence.\n\n' +
        'The universal rules still apply:\n' +
        '• Short-stacked it is all-in or fold - small raises are the worst of both worlds\n' +
        '• Attack the blinds from late position; that is where the free chips live\n' +
        '• Check the price before every call: pot odds vs your equity',
      followUpQuestions: ['Review my session', 'How should I play a short stack?', 'Give me a practice scenario'],
    };
  }

  let content = `**Beating ${challenge.title}**\n\n`;
  if (challenge.mission) content += `The mission: *${challenge.mission}*\n\n`;

  if (challenge.tips?.length) {
    content += `**The winning playbook:**\n`;
    challenge.tips.forEach((tip: string, i: number) => {
      content += `${i + 1}. ${tip}\n`;
    });
    content += `\n`;
  }

  // Personal gaps: where the player's own last run broke from the playbook.
  const keyHands: any[] = (report as any)?.keyHands || [];
  const gapLines: string[] = [];
  keyHands.forEach(h => {
    const deep = coachKeyHand(h, challenge);
    if (deep?.issue) {
      gapLines.push(`• Hand #${h.handNumber}: ${deep.text}`);
    }
  });
  if (gapLines.length > 0) {
    content += `**Where your last run broke from the playbook:**\n`;
    content += gapLines.slice(0, 3).join('\n') + '\n\n';
  } else if (keyHands.length > 0) {
    content += `Your last run followed the playbook - the loss was variance, not strategy. Run it back.\n\n`;
  }

  // Goal-type execution advice.
  const goalType = challenge.goalType || 'chips';
  content += `**Execution:**\n`;
  if (goalType === 'preflopWins') {
    content += `Only uncalled preflop raises count toward the goal. Raise larger than minimum against sticky bots (they fold more to bigger sizing), target the tightest stacks' blinds, and when a raise gets called, play the hand straightforwardly - the mission chip comes from the NEXT steal, not from forcing this one.\n`;
  } else if (goalType === 'survive') {
    content += `Survival pays for discipline, not heroics. Skip marginal calls entirely, steal just enough to outrun the blind increases, and remember the goal line counts at the END - protect the stack, don't grow it.\n`;
  } else {
    content += `Chip goals reward controlled aggression. Blinds jump every few hands, so early patience is cheap and late patience is expensive - pick your double-up spot before the blinds force one on you, and when you commit, jam rather than nibble.\n`;
  }

  return {
    type: 'analysis',
    title: `How to Beat ${challenge.title}`,
    content,
    followUpQuestions: [
      'What was my biggest mistake?',
      'Give me a practice scenario',
      'How should I play a short stack?',
    ],
  };
}

// ============================================================================
// DEFINITION RESPONSE
// ============================================================================

function generateDefinitionResponse(term?: string): CoachResponse {
  if (!term) {
    return {
      type: 'text',
      content: 'What poker term would you like me to explain? Try asking:\n\n' +
        '• "What is VPIP?"\n' +
        '• "Explain pot odds"\n' +
        '• "What is a continuation bet?"',
      followUpQuestions: ['What is VPIP?', 'Explain pot odds', 'What is equity?'],
    };
  }
  
  // Normalize term for lookup
  const normalizedTerm = term.toLowerCase()
    .replace(/\s+/g, '')
    .replace(/-/g, '')
    .replace('continuationbet', 'cbet');
  
  // Extended definitions with optimal values
  const extendedDefinitions: Record<string, { 
    def: string; 
    optimal?: string;
    example: string;
    tips?: string[];
  }> = {
    'vpip': {
      def: 'VPIP (Voluntarily Put $ In Pot) measures what percentage of hands you play preflop. It includes limping, calling, and raising - any time you voluntarily put money in before the flop.',
      optimal: '**Optimal 6-Max:** 19-28% | **Optimal Full Ring:** 12-20%',
      example: 'If you play 24 out of 100 hands, your VPIP is 24%.',
      tips: [
        'High VPIP (>30%) = Playing too many hands',
        'Low VPIP (<15%) = Missing profitable spots',
        'Your VPIP should vary by position - tighter from EP, looser from BTN',
      ],
    },
    'pfr': {
      def: 'PFR (Pre-Flop Raise) measures how often you raise before the flop. This includes open-raises and 3-bets.',
      optimal: '**Optimal 6-Max:** 16-24% | Should be ~4 points below VPIP',
      example: 'If you raise 20 out of 100 hands preflop, your PFR is 20%.',
      tips: [
        'PFR should be close to VPIP (gap < 6 points)',
        'Wide gap = Too much limping/calling',
        'Higher PFR = More aggressive, harder to play against',
      ],
    },
    'cbet': {
      def: 'A C-Bet (Continuation Bet) is when the preflop raiser bets again on the flop. It\'s called "continuation" because you\'re continuing the aggression you showed preflop.',
      optimal: '**Optimal:** 50-75% in heads-up pots',
      example: 'You raise preflop, one player calls, flop comes K72. You bet again - that\'s a c-bet.',
      tips: [
        'C-bet more on dry boards (K72, A63)',
        'C-bet less on wet boards (987, JT9)',
        'Use smaller sizing (1/3 pot) to c-bet more hands',
      ],
    },
    'aggression': {
      def: 'Aggression Factor (AF) measures how aggressive you are postflop. It\'s calculated as (Bets + Raises) / Calls.',
      optimal: '**Optimal:** 2.0-3.5',
      example: 'If you bet/raise 30 times and call 10 times, your AF is 3.0.',
      tips: [
        'AF < 1.5 = Too passive, calling station territory',
        'AF 2-3 = Balanced, solid player',
        'AF > 4 = Potentially over-aggressive',
      ],
    },
    'af': {
      def: 'Aggression Factor (AF) measures how aggressive you are postflop. It\'s calculated as (Bets + Raises) / Calls.',
      optimal: '**Optimal:** 2.0-3.5',
      example: 'If you bet/raise 30 times and call 10 times, your AF is 3.0.',
      tips: [
        'AF < 1.5 = Too passive, calling station territory',
        'AF 2-3 = Balanced, solid player',
        'AF > 4 = Potentially over-aggressive',
      ],
    },
    'wtsd': {
      def: 'WTSD (Went To ShowDown) measures how often you go to showdown after seeing a flop.',
      optimal: '**Optimal:** 24-32%',
      example: 'If you see 100 flops and go to showdown 27 times, your WTSD is 27%.',
      tips: [
        'High WTSD (>35%) = Calling station, hard to bluff',
        'Low WTSD (<22%) = Folding too much, easy to bluff',
        'Use WTSD with W$SD to see the full picture',
      ],
    },
    'wsd': {
      def: 'W$SD (Won $ at ShowDown) measures how often you win money when you reach showdown.',
      optimal: '**Optimal:** 49-58%',
      example: 'If you go to showdown 100 times and win 52 of them, your W$SD is 52%.',
      tips: [
        'Low W$SD = Taking weak hands to showdown',
        'High W$SD might mean folding too much preemptively',
        'WTSD 27% with W$SD 55% is often optimal',
      ],
    },
    'potodds': {
      def: 'Pot odds compare the size of the pot to the cost of a potential call. They help you decide if calling is mathematically profitable.',
      optimal: 'If your equity > pot odds, calling is profitable',
      example: 'Pot is $100, opponent bets $50. You need to call $50 to win $150. Pot odds = 50/150 = 33%.',
      tips: [
        'Compare pot odds to your equity (chance to win)',
        'If equity > pot odds, you have a profitable call',
        'Consider implied odds for drawing hands',
      ],
    },
    'equity': {
      def: 'Equity is your percentage chance to win the hand if all cards were dealt out. It represents your "share" of the pot.',
      optimal: 'You want to put money in when you have more equity than pot odds require',
      example: 'With a flush draw on the flop, you have about 35% equity (9 outs × ~4).',
      tips: [
        'Flush draw = ~35% equity on flop',
        'Open-ended straight draw = ~32% equity',
        'Gutshot = ~17% equity',
      ],
    },
    'outs': {
      def: 'Outs are the cards that will improve your hand to a likely winner.',
      example: 'With a flush draw (4 to a flush), you have 9 outs (13 cards of that suit minus 4 you can see).',
      tips: [
        'Flush draw = 9 outs (~35% by river)',
        'Open-ended straight draw = 8 outs (~32%)',
        'Gutshot = 4 outs (~17%)',
        'Rule of 2 and 4: Multiply outs by 2 (turn) or 4 (turn+river)',
      ],
    },
    'position': {
      def: 'Position refers to where you sit relative to the dealer button. Late position (BTN, CO) acts last and has more information.',
      optimal: 'Play more hands in late position, fewer in early position',
      example: 'On the Button, you act last on every postflop street - a huge advantage.',
      tips: [
        'BTN is the most profitable position',
        'Blinds are the least profitable',
        'Open tighter from UTG, looser from BTN',
        '"Position is power" - you gain information by acting last',
      ],
    },
    '3bet': {
      def: 'A 3-bet is a re-raise against an open-raise. The blinds are the first "bet," the open-raise is the second, and your re-raise is the third.',
      optimal: '**Optimal 3-bet %:** 5-10%',
      example: 'UTG raises to $6, you re-raise to $20 from the Button - that\'s a 3-bet.',
      tips: [
        '3-bet for value with premium hands (AA-QQ, AK)',
        '3-bet as a bluff with hands like A5s, 76s',
        '3-bet more in position, less out of position',
      ],
    },
    'mdf': {
      def: 'MDF (Minimum Defense Frequency) is the minimum percentage of hands you must continue with to prevent an opponent from profiting with any two cards.',
      optimal: 'MDF = 1 - (bet size / (pot + bet size))',
      example: 'Against a pot-sized bet, MDF = 50%. You need to call/raise with at least half your range.',
      tips: [
        'Half-pot bet → MDF = 67%',
        'Pot-sized bet → MDF = 50%',
        '2x pot bet → MDF = 33%',
      ],
    },
    'gto': {
      def: 'GTO (Game Theory Optimal) is a strategy that cannot be exploited. It involves balanced ranges of value bets and bluffs.',
      example: 'A GTO river bet might be 70% value hands and 30% bluffs - balanced so opponents can\'t gain by always calling or always folding.',
      tips: [
        'GTO is unexploitable but not maximum EV against weak players',
        'Exploit weak players instead of playing pure GTO',
        'Use GTO as a baseline, then deviate to exploit',
      ],
    },
    'impliedodds': {
      def: 'Implied odds factor in future bets you might win if you hit your hand. They let you call with worse pot odds.',
      example: 'You have a set draw. Direct odds don\'t justify calling, but if you hit, you\'ll likely stack your opponent - good implied odds.',
      tips: [
        'Sets and flush draws have great implied odds',
        'Implied odds are worse against short stacks',
        'They\'re better when opponent has a strong range',
      ],
    },
  };
  
  // Check extended definitions first
  const ext = extendedDefinitions[normalizedTerm];
  if (ext) {
    let content = ext.def + '\n\n';
    if (ext.optimal) content += ext.optimal + '\n\n';
    content += `**Example:** ${ext.example}`;
    
    return {
      type: 'glossary',
      title: term.toUpperCase(),
      content,
      details: ext.tips ? ext.tips.map(t => `• ${t}`).join('\n') : undefined,
      followUpQuestions: [
        `Show me a ${term} scenario`,
        'What other stats should I track?',
        'How can I improve this?',
      ],
    };
  }
  
  // Check glossary
  for (const [key, entry] of Object.entries(POKER_GLOSSARY)) {
    if (key.toLowerCase().includes(normalizedTerm) || 
        entry.term.toLowerCase().includes(term.toLowerCase())) {
      return {
        type: 'glossary',
        title: entry.term,
        content: entry.definition,
        details: entry.example,
        relatedTerms: getRelatedTerms(key),
        followUpQuestions: [`Show me a ${key} scenario`, 'What other terms should I learn?'],
      };
    }
  }
  
  // Basic definitions
  const basicDefinitions: Record<string, { def: string; example: string }> = {
    'fold': {
      def: 'To discard your hand and forfeit the current pot. You stop putting money in and wait for the next hand.',
      example: 'You have 7-2 offsuit and someone raises. You fold because it\'s a terrible hand.',
    },
    'call': {
      def: 'To match the current bet amount to stay in the hand.',
      example: 'Someone bets $10, you call by putting in $10 to see the next card.',
    },
    'raise': {
      def: 'To increase the bet amount, forcing others to put in more money or fold.',
      example: 'Someone bets $10, you raise to $30. They must put in $20 more to continue.',
    },
    'check': {
      def: 'To pass the action without betting when no one has bet before you.',
      example: 'First to act on the flop with no bets yet? You can check and see what others do.',
    },
    'bluff': {
      def: 'Betting or raising with a weak hand to make opponents fold better hands.',
      example: 'You have nothing on the river but bet big representing a strong hand, hoping they fold.',
    },
    'allin': {
      def: 'To bet all your remaining chips. You can\'t be forced to fold if you\'re all-in.',
      example: 'You have $100 left and push it all in. Even if they bet more, you can still win the main pot.',
    },
  };
  
  const basic = basicDefinitions[normalizedTerm];
  if (basic) {
    return {
      type: 'glossary',
      title: term.charAt(0).toUpperCase() + term.slice(1),
      content: basic.def,
      details: `**Example:** ${basic.example}`,
    };
  }
  
  return {
    type: 'text',
    content: `I don't have a definition for "${term}" in my glossary. Here are some terms I can explain:`,
    details: Object.values(POKER_GLOSSARY).slice(0, 8).map(e => `• ${e.term}`).join('\n'),
    followUpQuestions: ['What is VPIP?', 'Explain pot odds', 'What is equity?'],
  };
}

function getRelatedTerms(key: string): string[] {
  const relationships: Record<string, string[]> = {
    'vpip': ['pfr', 'position', 'range'],
    'pfr': ['vpip', 'aggression', 'cbet'],
    'position': ['vpip', 'range', 'steal'],
    'cbet': ['pfr', 'boardTexture', 'aggression'],
    'potOdds': ['equity', 'outs', 'impliedOdds'],
    'equity': ['potOdds', 'outs', 'range'],
    'outs': ['equity', 'potOdds'],
    'impliedOdds': ['potOdds', 'spr'],
    'semiBluff': ['equity', 'outs', 'bluffing'],
    'boardTexture': ['cbet', 'range'],
    'aggression': ['pfr', 'cbet', 'af'],
    'gto': ['mdf', 'range', 'balance'],
    'range': ['gto', 'position', 'equity'],
    'mdf': ['gto', 'potOdds'],
  };
  return relationships[key] || [];
}

// ============================================================================
// HAND-LEVEL COACHING - real analysis, not summary narration
// ============================================================================
// Uses the deep decision data on each key hand (stack depth in bb, position,
// equity, pot odds) to say what the winning play was and WHY, with numbers.
// Short-stack spots get push/fold range analysis; postflop spots get
// equity-vs-price math.

const COACH_RANKS = '23456789TJQKA';

interface ParsedHand {
  label: string;      // "A4o", "53o", "TT"
  isPair: boolean;
  suited: boolean;
  hi: number;         // 0-12 index into COACH_RANKS
  lo: number;
}

function parseHoleCards(cards: string[] = []): ParsedHand | null {
  if (cards.length !== 2) return null;
  const norm = (c: string) => {
    const rank = c.slice(0, -1).toUpperCase().replace('10', 'T');
    return { rank, suit: c.slice(-1).toLowerCase() };
  };
  const a = norm(cards[0]);
  const b = norm(cards[1]);
  const ia = COACH_RANKS.indexOf(a.rank);
  const ib = COACH_RANKS.indexOf(b.rank);
  if (ia < 0 || ib < 0) return null;
  const hi = Math.max(ia, ib);
  const lo = Math.min(ia, ib);
  const isPair = ia === ib;
  const suited = a.suit === b.suit;
  const label = isPair
    ? `${COACH_RANKS[hi]}${COACH_RANKS[hi]}`
    : `${COACH_RANKS[hi]}${COACH_RANKS[lo]}${suited ? 's' : 'o'}`;
  return { label, isPair, suited, hi, lo };
}

// Jam tier: how strong is this hand as a short-stack shove?
// 4 = premium (jam any position, any short stack)
// 3 = strong (jam ≤12bb from any position, ≤15bb from MP on)
// 2 = standard late-position ammo (jam ≤10bb from CO/BTN/SB)
// 1 = wide button/small-blind jam (jam ≤8bb from BTN/SB, or ≤10bb SB vs BB)
// 0 = never a jam at meaningful depth
function jamTier(h: ParsedHand): number {
  const R = (r: string) => COACH_RANKS.indexOf(r);
  const A = R('A'), K = R('K'), Q = R('Q'), J = R('J'), T = R('T'), NINE = R('9'), SEVEN = R('7'), FIVE = R('5');
  if (h.isPair) {
    if (h.hi >= NINE) return 4;                    // 99+
    if (h.hi >= FIVE) return 3;                    // 55-88
    return 2;                                      // 22-44
  }
  if (h.hi === A) {
    if (h.lo >= Q) return 4;                       // AQ+
    if (h.lo >= NINE || h.suited) return 3;        // A9+, any suited ace
    return 2;                                      // A2o-A8o
  }
  if (h.hi === K) {
    if (h.lo >= Q) return 3;                       // KQ
    if (h.lo >= NINE || (h.suited && h.lo >= SEVEN)) return 2; // K9+, K7s+
    return h.suited ? 1 : 0;
  }
  if (h.hi === Q) {
    if (h.lo >= T && h.suited) return 2;           // QTs+
    if (h.lo >= T) return 1;                       // QTo, QJo
    return h.suited && h.lo >= R('8') ? 1 : 0;
  }
  if (h.hi === J || h.hi === T) {
    if (h.suited && h.lo >= h.hi - 1) return 2;    // JTs, T9s
    if (h.suited && h.lo >= h.hi - 2) return 1;    // J9s, T8s
    if (!h.suited && h.lo >= T) return 1;          // JTo
    return 0;
  }
  if (h.suited && h.hi - h.lo === 1 && h.lo >= R('6')) return 1; // 98s, 87s, 76s
  return 0;
}

// Is this position allowed to jam at this tier/depth?
function jamVerdict(tier: number, stackBB: number, position: string): 'jam' | 'edge' | 'fold' {
  const pos = position.toUpperCase();
  const late = pos === 'CO' || pos === 'BTN' || pos === 'SB';
  if (tier >= 4) return 'jam';
  if (tier === 3) return stackBB <= 15 ? 'jam' : 'edge';
  if (tier === 2) return stackBB <= 10 && late ? 'jam' : stackBB <= 12 && late ? 'edge' : 'fold';
  if (tier === 1) return stackBB <= 8 && (pos === 'BTN' || pos === 'SB') ? 'jam'
    : stackBB <= 10 && pos === 'SB' ? 'edge' : 'fold';
  return 'fold';
}

// Detailed per-hand coaching. Returns the explanation plus an issue tag
// (for the prioritized fix list) - or null when there isn't enough data to
// say something real.
function coachKeyHand(h: any, challenge: any): { text: string; issue?: string } | null {
  const hand = parseHoleCards(h.holeCards);
  if (!hand) return null;
  const bb = h.bigBlind || 0;
  const stack = h.stackSize || h.effectiveStack || 0;
  const stackBB = bb > 0 && stack > 0 ? Math.round(stack / bb) : 0;
  const action = String(h.actionRaw || h.userAction || '').toLowerCase();
  const pos = String(h.position || '').toUpperCase();
  const preflop = h.street === 'preflop';

  // ── Short-stack preflop: push/fold analysis ──
  if (preflop && stackBB > 0 && stackBB <= 15) {
    const tier = jamTier(hand);
    const verdict = jamVerdict(tier, stackBB, pos);
    const depth = `${stackBB}bb`;

    if (action.includes('all-in')) {
      if (verdict === 'jam') {
        return { text: `Standard jam. At ${depth}, ${hand.label} from the ${pos} is inside the shoving range: you pick up the blinds${h.facingRaise ? '' : ' uncontested'} often enough, and you still have live equity when called. This is exactly what the drill trains.` };
      }
      if (verdict === 'edge') {
        return { text: `Thin but defensible. ${hand.label} at ${depth} from the ${pos} is the bottom of the jamming range - fine when the table is folding a lot, a fold when the callers behind are loose. Know that you're choosing the aggressive side of a close spot.` };
      }
      return { issue: 'punt-jam', text: `This is the punt the drill warns about. ${hand.label} is nowhere near a ${depth} jamming range from the ${pos} - when called you're roughly a 2-to-1 underdog with no blocker help, and at ${depth} you still had time to wait. The winning line: fold, and save the jam for any pair, any ace, or K9+ (wider from the button and small blind).` };
    }

    if (action.includes('raise')) {
      const raisePct = stack > 0 ? Math.round(((h.betAmount || 0) / stack) * 100) : 0;
      if (verdict === 'jam' || verdict === 'edge') {
        return { issue: 'small-raise', text: `Right hand, wrong sizing. ${hand.label} at ${depth} from the ${pos} plays better as a jam than a raise to ${h.betAmount || '?'} (${raisePct}% of your stack): the small raise invites a rejam that forces you into a horrible call-or-fold, while the jam puts maximum pressure on and can't be outplayed. At this depth: all-in or fold, nothing in between.` };
      }
      return { issue: 'small-raise', text: `Two mistakes in one: ${hand.label} isn't strong enough to play at ${depth} from the ${pos}, and the small raise is the worst way to play it - you've committed ${raisePct}% of your stack with a hand that can't stand a rejam. Fold this one; when you do play, jam.` };
    }

    if (action.includes('call') && h.facingRaise) {
      return { issue: 'flat-call', text: `Flat-calling at ${depth} is the cardinal sin of short-stack play: you put chips in with no fold equity and no plan for most flops. With ${hand.label} facing a raise, the choice is rejam (if it's near the top of your range) or fold - never call.` };
    }

    if (action.includes('fold')) {
      if (verdict === 'jam' && tier >= 3) {
        return { issue: 'too-tight', text: `Too tight. ${hand.label} at ${depth} from the ${pos} is a clearly profitable jam - every fold here costs you 1.5bb per orbit in blinds while you wait for a hand that may never come. Winners jam this and take the pot or the flip.` };
      }
      if (verdict === 'jam' || verdict === 'edge') {
        return { text: `Slightly too tight - ${hand.label} at ${depth} from the ${pos} is a standard jam for most winning short-stackers, though folding it isn't a disaster. If your stack drops further, hands like this become mandatory.` };
      }
      return { text: `Correct fold. ${hand.label} doesn't make any ${depth} shoving range from the ${pos} - the discipline to keep mucking these is what keeps your stack alive for the real jam spots.` };
    }
  }

  // ── Deep-stack preflop: hand selection and value lines ──
  if (preflop && stackBB > 15) {
    const tier = jamTier(hand);
    if ((action.includes('raise') || action.includes('bet')) && tier >= 3) {
      return { text: `Standard value raise. ${hand.label} at ${stackBB}bb wants a bigger pot while it's ahead - raising sets up value bets on multiple streets, which is exactly the deep-stack line winners take.` };
    }
    if (action.includes('raise') && tier <= 1 && !h.facingRaise) {
      return { text: `Aggressive pick with ${hand.label} - fine as an occasional steal from late position, but keep the frequency low deep-stacked: weak hands play awkward pots when called.` };
    }
    if (action.includes('fold') && tier >= 4 && !h.facingRaise) {
      return { issue: 'too-tight', text: `${hand.label} is a premium hand - folding it preflop without pressure gives away the exact spots this challenge pays you to take.` };
    }
    if (action.includes('fold') && tier === 0) {
      return { text: `Correct fold. ${hand.label} has no business in the pot even ${stackBB}bb deep - ruthless hand selection is half of winning deep-stack play.` };
    }
    if (action.includes('call') && hand.isPair && hand.hi <= COACH_RANKS.indexOf('8') && h.facingRaise) {
      return { text: `Good set-mine. Small pairs call cheap raises when deep because a flopped set wins a stack-sized pot - just discipline yourself to fold the flop when you miss.` };
    }
  }

  // ── Postflop with a price: equity vs pot odds ──
  if (!preflop && h.facingBet && (h.requiredEquity || 0) > 0 && (h.equity || 0) > 0) {
    const eq = Math.round(h.equity);
    const req = Math.round(h.requiredEquity);
    const desc = h.handDescription ? ` with ${h.handDescription.toLowerCase()}` : '';
    if (action.includes('call')) {
      return eq >= req
        ? { text: `Solid call: you needed about ${req}% equity for the price and had roughly ${eq}%${desc}. That's a profitable call every time you make it.` }
        : { issue: 'bad-call', text: `The math says fold: the price required about ${req}% equity and you had roughly ${eq}%${desc}. Calls like this leak chips slowly - each one costs a little, and they add up across a session.` };
    }
    if (action.includes('fold')) {
      return eq > req + 10
        ? { issue: 'missed-price', text: `This fold left value behind: you had roughly ${eq}% equity${desc} and only needed about ${req}% for the price. When the pot lays you the right odds, the call prints - even when this particular one loses.` }
        : { text: `Reasonable fold - roughly ${eq}% equity${desc} against a price needing about ${req}%. No need to pay for a draw that isn't priced in.` };
    }
  }

  // ── Postflop aggression notes ──
  if (!preflop && (action.includes('bet') || action.includes('raise')) && h.handDescription) {
    return { text: `Betting ${h.handDescription.toLowerCase()} here builds the pot while you're likely ahead and denies free cards to draws - the aggressive line the stats reward.` };
  }

  return null;
}

// ============================================================================
// SESSION REVIEW RESPONSE (ENHANCED)
// ============================================================================

function generateSessionReviewResponse(report?: TrainingReport): CoachResponse {
  if (!report) {
    return {
      type: 'text',
      title: 'No Session Data Available',

      content: 'I don\'t have access to your session data yet. Complete a training session first, then come back and I can analyze your play!\n\n' +
        '**In the meantime, I can help you with:**\n' +
        '• Poker concepts and definitions\n' +
        '• Practice scenarios\n' +
        '• Strategy questions',
      followUpQuestions: [
        'Start a training session',
        'Give me a practice scenario',
        'What should I focus on as a beginner?',
      ],
    };
  }
  
  // 🃏 SMALL SAMPLE: stats are meaningless off a handful of hands - VPIP of
  // 100% after one hand says nothing about anyone. Skip the profile lecture
  // and talk through the actual hands from the session instead.
  const keyHands: any[] = (report as any).keyHands || [];
  const fmtCards = (cards: string[] = []) =>
    cards.map(c => {
      const glyphs: Record<string, string> = { h: '♥', d: '♦', c: '♣', s: '♠' };
      return `${c.slice(0, -1).toUpperCase()}${glyphs[c.slice(-1).toLowerCase()] || ''}`;
    }).join(' ');

  // 🏆 Challenge context: when the session came from a Tournament Training
  // challenge, every hand gets judged against THAT challenge's winning
  // approach, and the coach can say exactly how the spot should be played.
  const challenge: any = (report as any).challenge || null;

  const runOutcome: any = (report as any).challengeOutcome;

  if ((report.handsPlayed || 0) < 10) {
    const n = report.handsPlayed || keyHands.length || 0;
    let content = '';
    if (challenge && runOutcome?.outcome === 'won') {
      content += `**You BEAT ${challenge.title}.** `;
      content += `Finished at ${Number(runOutcome.heroChips || 0).toLocaleString()} chips in ${runOutcome.handsPlayed} hand${runOutcome.handsPlayed === 1 ? '' : 's'}`;
      if (challenge.goalChips) content += ` against a ${Number(challenge.goalChips).toLocaleString()}-chip goal`;
      content += `. Winning runs deserve review too - locking in WHY it worked is how it becomes repeatable. Hand by hand:\n\n`;
    } else if (challenge) {
      content += `This was your **${challenge.title}** run`;
      if (challenge.mission) content += ` - the mission was: *${challenge.mission}*`;
      content += `.`;
      if (runOutcome?.outcome === 'lost') {
        content += ` The run came up short at ${Number(runOutcome.heroChips || 0).toLocaleString()} chips - let's find exactly where.`;
      }
      content += `\n\n`;
      content += `${n} hand${n === 1 ? '' : 's'} is too small a sample for style statistics, so let's judge each hand against what this challenge is training${challenge.skill ? ` (${challenge.skill})` : ''}.\n\n`;
    } else {
      content += `This session was only ${n} hand${n === 1 ? '' : 's'}, which is far too small a sample to judge your style from statistics - one hand can make anyone look wild. So instead of a stat lecture, let's talk about what actually happened.\n\n`;
    }

    if (keyHands.length > 0) {
      const issues: string[] = [];
      keyHands.slice(0, 6).forEach(h => {
        content += `**Hand #${h.handNumber}: ${fmtCards(h.holeCards)}`;
        if (h.position) content += ` in the ${h.position}`;
        const depthBB = h.bigBlind > 0 && (h.stackSize || h.effectiveStack) > 0
          ? Math.round((h.stackSize || h.effectiveStack) / h.bigBlind) : 0;
        if (depthBB > 0) content += ` · ${depthBB}bb deep`;
        content += `**\n`;
        if (h.board?.length) content += `Board: ${fmtCards(h.board)}\n`;
        // Full action line - every street, not just the key decision.
        if (h.actions?.length > 1) {
          content += `Your line: ${h.actions.map((a: any) =>
            `${a.street} ${a.action}${a.amount ? ` ${Number(a.amount).toLocaleString()}` : ''}`
          ).join(' → ')}\n`;
        } else {
          content += `You chose **${h.userAction}**${h.potSize ? ` with ${Number(h.potSize).toLocaleString()} in the pot` : ''}.\n`;
        }
        if (typeof h.won === 'boolean') {
          const amt = Math.abs(h.profit || 0);
          content += `Result: ${h.won ? 'won' : 'lost'}${amt ? ` ${amt.toLocaleString()} chips` : ''}${h.showdown ? ' at showdown' : ''}.\n`;
        }
        const deep = coachKeyHand(h, challenge);
        if (deep) {
          content += `${deep.text}\n\n`;
          if (deep.issue) issues.push(deep.issue);
        } else {
          content += `${h.explanation || ''}\n\n`;
        }
      });

      // Prioritized fix list - the biggest leak first, counted across hands.
      if (issues.length > 0) {
        const ISSUE_FIXES: Record<string, string> = {
          'punt-jam': 'Stop jamming junk. Below 15bb, shove pairs, aces, K9+/KQ, and suited broadways - wider from the button and small blind, but never bottom-tier offsuit hands.',
          'small-raise': 'Kill the small raise. Under about 12bb it is all-in or fold - a small raise commits chips you cannot back up and hands opponents a perfect rejam.',
          'flat-call': 'Never flat-call a raise short-stacked. Rejam your strong hands for maximum pressure, fold the rest.',
          'too-tight': 'Loosen your jam range. Folding clearly profitable shoves bleeds 1.5bb every orbit - patience is good, paralysis is fatal.',
          'bad-call': 'Check the price before calling. Compare the equity you need for the pot odds against what your hand actually has.',
          'missed-price': 'Take the right price when the pot offers it - correctly priced calls make money even when the card misses.',
        };
        const counts = new Map<string, number>();
        issues.forEach(i => counts.set(i, (counts.get(i) || 0) + 1));
        const ordered = [...counts.entries()].sort((a, b) => b[1] - a[1]);
        content += `**Your fix list (biggest leak first):**\n`;
        ordered.forEach(([issue, cnt], i) => {
          content += `${i + 1}. ${ISSUE_FIXES[issue] || issue}${cnt > 1 ? ` (came up ${cnt} times this run)` : ''}\n`;
        });
        content += `\n`;
      } else if (runOutcome?.outcome === 'won') {
        content += `Clean run: no leaks in the sample AND the result followed. That combination is the whole point - repeat this pattern and the wins stop being luck.\n\n`;
      } else {
        content += `No real leaks in this sample - the decisions held up against the challenge's playbook.\n\n`;
      }

      if (challenge?.tips?.length) {
        content += `**How winners beat ${challenge.title}:**\n`;
        challenge.tips.forEach((tip: string, i: number) => {
          content += `${i + 1}. ${tip}\n`;
        });
        content += `\nMatch your hands above against that playbook - that's the gap to close on the next run.\n`;
      }
      content += `\nOnce you've logged 10+ hands in a session, I can profile your overall style with real statistics.\n`;
    } else {
      content += `I don't have the hand details for this one, but play another session and I'll walk through every hand with you.\n`;
    }

    return {
      type: 'analysis',
      title: challenge
        ? `${challenge.title} - ${runOutcome?.outcome === 'won' ? 'Winning Run Review' : 'Run Review'}`
        : 'Session Review',
      content,
      followUpQuestions: challenge ? [
        `How do I beat ${challenge.title}?`,
        'Give me a practice scenario',
        'How should I play a short stack?',
      ] : [
        'Give me a practice scenario',
        'How should I play a short stack?',
        'What hands should I play from my position?',
      ],
    };
  }

  const leaks = detectLeaks(report);
  const playerType = classifyPlayerType(
    report.stats.vpip,
    report.stats.pfr,
    report.stats.aggressionFactor
  );
  const opt = OPTIMAL_STATS.sixMax;

  // Build detailed analysis
  let content = `**Session Review** (Score: ${report.overallScore.toFixed(1)}/10)\n\n`;

  if (challenge) {
    content += `**Challenge:** ${challenge.title}`;
    if (challenge.mission) content += ` - *${challenge.mission}*`;
    content += `\n\n`;
  }

  content += `**Player Profile:** ${playerType.label}\n`;
  content += `*${playerType.description}*\n\n`;
  
  content += `** Your Stats vs Optimal:**\n`;

  
  // VPIP Analysis
  const vpipStatus = report.stats.vpip < opt.vpip.min ? ' Low' : 

                     report.stats.vpip > opt.vpip.max ? ' High' : ' Good';

  content += `• **VPIP:** ${report.stats.vpip.toFixed(0)}% ${vpipStatus} (Optimal: ${opt.vpip.min}-${opt.vpip.max}%)\n`;
  
  // PFR Analysis  
  const pfrStatus = report.stats.pfr < opt.pfr.min ? ' Low' : 

                    report.stats.pfr > opt.pfr.max ? ' High' : ' Good';

  content += `• **PFR:** ${report.stats.pfr.toFixed(0)}% ${pfrStatus} (Optimal: ${opt.pfr.min}-${opt.pfr.max}%)\n`;
  
  // Gap Analysis
  const gap = report.stats.vpip - report.stats.pfr;
  const gapStatus = gap > opt.vpipPfrGap.max ? ' Too Wide' : ' Good';

  content += `• **VPIP/PFR Gap:** ${gap.toFixed(0)} ${gapStatus} (Optimal: ${opt.vpipPfrGap.min}-${opt.vpipPfrGap.max})\n`;
  
  // C-Bet Analysis
  const cbetStatus = report.stats.cbetFrequency < opt.cbet.min ? ' Low' : 

                     report.stats.cbetFrequency > opt.cbet.max ? ' High' : ' Good';

  content += `• **C-Bet:** ${report.stats.cbetFrequency.toFixed(0)}% ${cbetStatus} (Optimal: ${opt.cbet.min}-${opt.cbet.max}%)\n`;
  
  // Aggression Analysis
  const aggStatus = report.stats.aggressionFactor < opt.aggFactor.min ? ' Low' : 

                    report.stats.aggressionFactor > opt.aggFactor.max ? ' High' : ' Good';

  content += `• **Aggression:** ${report.stats.aggressionFactor.toFixed(1)} ${aggStatus} (Optimal: ${opt.aggFactor.min}-${opt.aggFactor.max})\n`;
  
  content += '\n';
  
  // Critical Issues
  const criticalLeaks = leaks.filter(l => l.severity === 'critical');
  if (criticalLeaks.length > 0) {
    content += `** Critical Issues (Fix These First!):**\n`;

    criticalLeaks.forEach(leak => {
      content += ` **${leak.title}**\n`;

      content += `${leak.description}\n`;
      content += `→ ${leak.fix[0]}\n\n`;
    });
  }
  
  // Areas to Improve
  const majorLeaks = leaks.filter(l => l.severity === 'major');
  if (majorLeaks.length > 0) {
    content += `** Areas to Improve:**\n`;

    majorLeaks.forEach(leak => {
      content += `• ${leak.title}\n`;
    });
    content += '\n';
  }
  
  // Recommended Drills
  if (leaks.length > 0) {
    content += `** Recommended Drills:**\n`;

    const drills = leaks.slice(0, 2).flatMap(l => l.drills.slice(0, 1));
    drills.forEach(drill => {
      content += `${drill}\n`;
    });
  }

  // Ground the numbers in real hands: the most teachable spots first.
  if (keyHands.length > 0) {
    const teachable = [...keyHands].sort((a, b) => Number(b.isMistake) - Number(a.isMistake)).slice(0, 3);
    content += `\n**Hands worth another look:**\n`;
    teachable.forEach(h => {
      const deep = coachKeyHand(h, challenge);
      content += `• Hand #${h.handNumber} (${fmtCards(h.holeCards)}${h.position ? `, ${h.position}` : ''}): you chose ${h.userAction}. ${deep ? deep.text : h.explanation}\n`;
    });
  }

  if (challenge?.tips?.length) {
    content += `\n**How winners beat ${challenge.title}:**\n`;
    challenge.tips.forEach((tip: string, i: number) => {
      content += `${i + 1}. ${tip}\n`;
    });
  }

  return {
    type: 'analysis',
    title: 'Session Analysis',

    content,
    leaks: criticalLeaks.slice(0, 3),
    followUpQuestions: [
      'Find all my leaks',
      'Create an improvement plan',
      criticalLeaks.length > 0 ? `Practice ${criticalLeaks[0].title.toLowerCase().includes('c-bet') ? 'c-betting' : 'preflop ranges'}` : 'Give me a scenario',
    ],
    priority: criticalLeaks.length > 0 ? 'critical' : majorLeaks.length > 0 ? 'high' : 'medium',
  };
}

// ============================================================================
// LEAK DETECTION RESPONSE
// ============================================================================

function generateLeakDetectionResponse(report?: TrainingReport): CoachResponse {
  if (!report) {
    return {
      type: 'text',
      content: 'I need your session data to find leaks. Complete a training session first!',
      followUpQuestions: ['Start a training session'],
    };
  }
  
  const leaks = detectLeaks(report);
  
  if (leaks.length === 0) {
    return {
      type: 'text',
      title: 'No Major Leaks Detected',

      content: 'Your stats are looking solid! No critical leaks found.\n\n' +
        'Keep practicing to maintain your edge and look for small optimizations.',
      followUpQuestions: ['Create an improvement plan', 'Give me an advanced scenario'],
    };
  }
  
  let content = `** Leak Analysis Found ${leaks.length} Issue${leaks.length > 1 ? 's' : ''}:**\n\n`;

  
  leaks.forEach((leak, i) => {
    const severity = leak.severity === 'critical' ? '' : leak.severity === 'major' ? '' : '';

    content += `**${i + 1}. ${severity} ${leak.title}** (${leak.severity.toUpperCase()})\n`;
    content += `${leak.description}\n\n`;
    content += `**Impact:** ${leak.impact}\n\n`;
    content += `**How to Fix:**\n`;
    leak.fix.forEach(f => content += `• ${f}\n`);
    content += '\n';
    content += `**Practice Drills:**\n`;
    leak.drills.forEach(d => content += `${d}\n`);
    content += '\n---\n\n';
  });
  
  return {
    type: 'analysis',
    title: 'Leak Detection Report',

    content,
    leaks,
    followUpQuestions: [
      'Create an improvement plan',
      leaks[0] ? `Practice ${leaks[0].category}` : 'Give me a scenario',
      'Explain ' + (leaks[0]?.title.includes('C-Bet') ? 'c-bet' : 'VPIP'),
    ],
    priority: leaks[0]?.severity === 'critical' ? 'critical' : 'high',
  };
}

// ============================================================================
// IMPROVEMENT PLAN RESPONSE
// ============================================================================

function generateImprovementPlanResponse(report?: TrainingReport): CoachResponse {
  if (!report) {
    return {
      type: 'text',
      content: 'I need your session data to create an improvement plan. Complete a training session first!',
      followUpQuestions: ['Start a training session'],
    };
  }
  
  const plan = generateImprovementPlan(report);
  
  let content = `**${plan.overallAssessment}**\n\n`;
  
  content += `**Player Type:** ${plan.playerType.label}\n`;
  content += `**Current Level:** ${plan.skillLevel}\n\n`;
  
  if (plan.strengthAreas.length > 0) {
    content += `** Your Strengths:**\n`;

    plan.strengthAreas.forEach(s => content += `• ${s}\n`);
    content += '\n';
  }
  
  if (plan.focusAreas.length > 0) {
    content += `** Focus Areas:**\n`;

    plan.focusAreas.forEach(f => content += `• ${f}\n`);
    content += '\n';
  }
  
  content += `** This Week's Goals:**\n`;

  plan.weeklyGoals.forEach((g, i) => content += `${i + 1}. ${g}\n`);
  content += '\n';
  
  content += `** Key Metrics to Track:**\n`;

  plan.progressMetrics.forEach(m => {
    const arrow = m.current < m.target ? '' : m.current > m.target ? '' : '';

    content += `• ${m.metric}: ${m.current.toFixed(1)} → ${m.target.toFixed(1)} ${arrow}\n`;
  });
  
  return {
    type: 'plan',
    title: 'Your Improvement Plan',

    content,
    improvementPlan: plan,
    followUpQuestions: [
      plan.recommendedScenarios[0] ? `Practice ${plan.recommendedScenarios[0].replace('-', ' ')}` : 'Give me a scenario',
      'Find my leaks',
      'Explain ' + (plan.leaks[0]?.title.includes('VPIP') ? 'VPIP' : 'c-bet'),
    ],
  };
}

// ============================================================================
// STAT ANALYSIS RESPONSE
// ============================================================================

function generateStatAnalysisResponse(statName?: string, report?: TrainingReport): CoachResponse {
  const opt = OPTIMAL_STATS.sixMax;
  
  const statInfo: Record<string, { 
    name: string;
    optimal: { min: number; max: number; ideal: number };
    description: string;
    tips: string[];
  }> = {
    'vpip': {
      name: 'VPIP (Voluntarily Put $ In Pot)',
      optimal: { min: opt.vpip.min, max: opt.vpip.max, ideal: opt.vpip.optimal },
      description: 'Percentage of hands you voluntarily play preflop',
      tips: [
        'Tighter from early position (12-15%)',
        'Looser from late position (35-50% on BTN)',
        'Wide gap from PFR indicates too much calling',
      ],
    },
    'pfr': {
      name: 'PFR (Pre-Flop Raise)',
      optimal: { min: opt.pfr.min, max: opt.pfr.max, ideal: opt.pfr.optimal },
      description: 'Percentage of hands you raise preflop',
      tips: [
        'Should be close to VPIP (gap < 6)',
        'Raise or fold - avoid limping',
        'Higher PFR = more aggressive image',
      ],
    },
    'cbet': {
      name: 'C-Bet (Continuation Bet)',
      optimal: { min: opt.cbet.min, max: opt.cbet.max, ideal: opt.cbet.optimal },
      description: 'How often you bet the flop as preflop raiser',
      tips: [
        'Bet more on dry boards (K72r)',
        'Check more on wet boards (JT9)',
        'Use 1/3 pot sizing to c-bet more',
      ],
    },
    'aggression': {
      name: 'Aggression Factor',
      optimal: { min: opt.aggFactor.min, max: opt.aggFactor.max, ideal: opt.aggFactor.optimal },
      description: 'Ratio of bets+raises to calls postflop',
      tips: [
        'AF < 2 = Too passive',
        'AF 2-3 = Well balanced',
        'AF > 4 = Possibly over-aggressive',
      ],
    },
    'af': {
      name: 'Aggression Factor',
      optimal: { min: opt.aggFactor.min, max: opt.aggFactor.max, ideal: opt.aggFactor.optimal },
      description: 'Ratio of bets+raises to calls postflop',
      tips: [
        'AF < 2 = Too passive',
        'AF 2-3 = Well balanced',
        'AF > 4 = Possibly over-aggressive',
      ],
    },
    'wtsd': {
      name: 'WTSD (Went to Showdown)',
      optimal: { min: opt.wtsd.min, max: opt.wtsd.max, ideal: opt.wtsd.optimal },
      description: 'How often you reach showdown after seeing flop',
      tips: [
        'High WTSD = Calling station, hard to bluff',
        'Low WTSD = Folding too much, exploitable',
        'Use with W$SD for full picture',
      ],
    },
  };
  
  const stat = statName ? statInfo[statName.toLowerCase().replace('-', '')] : null;
  
  if (!stat) {
    return {
      type: 'text',
      title: 'Stat Analysis',

      content: 'Which stat would you like me to analyze?\n\n' +
        '• VPIP - How many hands you play\n' +
        '• PFR - How often you raise preflop\n' +
        '• C-Bet - Continuation bet frequency\n' +
        '• Aggression - Postflop aggression factor\n' +
        '• WTSD - Went to showdown %',
      followUpQuestions: ['Analyze my VPIP', 'What is PFR?', 'Is my c-bet good?'],
    };
  }
  
  let content = `**${stat.name}**\n\n`;
  content += `*${stat.description}*\n\n`;
  content += `**Optimal Range:** ${stat.optimal.min} - ${stat.optimal.max}\n`;
  content += `**Ideal:** ~${stat.optimal.ideal}\n\n`;
  
  if (report) {
    const currentValue = statName === 'vpip' ? report.stats.vpip :
                         statName === 'pfr' ? report.stats.pfr :
                         statName === 'cbet' ? report.stats.cbetFrequency :
                         report.stats.aggressionFactor;
    
    const isInRange = currentValue >= stat.optimal.min && currentValue <= stat.optimal.max;
    const status = isInRange ? ' In Range' : 

                   currentValue < stat.optimal.min ? ' Below Optimal' : ' Above Optimal';

    
    content += `**Your ${stat.name.split(' ')[0]}:** ${currentValue.toFixed(1)} ${status}\n\n`;
    
    if (!isInRange) {
      if (currentValue < stat.optimal.min) {
        content += `**Recommendation:** Increase your ${statName} by ${(stat.optimal.ideal - currentValue).toFixed(0)} points.\n`;
      } else {
        content += `**Recommendation:** Decrease your ${statName} by ${(currentValue - stat.optimal.ideal).toFixed(0)} points.\n`;
      }
    }
  }
  
  content += '\n**Tips:**\n';
  stat.tips.forEach(t => content += `• ${t}\n`);
  
  return {
    type: 'analysis',
    title: `${stat.name} Analysis`,

    content,
    followUpQuestions: [
      `How do I ${report && statName === 'vpip' && report.stats.vpip > stat.optimal.max ? 'lower' : 'improve'} my ${statName}?`,
      'Find my leaks',
      'What other stats matter?',
    ],
  };
}

// ============================================================================
// POSITION ADVICE RESPONSE
// ============================================================================

function generatePositionAdviceResponse(position?: string): CoachResponse {
  const positionGuides: Record<string, {
    name: string;
    description: string;
    vpipRange: { min: number; max: number };
    strategy: string[];
    commonMistakes: string[];
    openingHands: string;
  }> = {
    'UTG': {
      name: 'Under The Gun (UTG)',
      description: 'First to act preflop. The worst position with 5+ players to act behind you.',
      vpipRange: { min: 12, max: 18 },
      strategy: [
        'Play VERY tight - only premium hands',
        'Open-raise to 3x with strong hands',
        'Fold marginal hands like KJo, QTs',
        'Your range should be strong enough to play multiway',
      ],
      commonMistakes: [
        'Opening too many hands',
        'Playing suited connectors (too risky here)',
        'Calling 3-bets with weak holdings',
      ],
      openingHands: 'AA, KK, QQ, JJ, TT, 99, AKs, AQs, AKo, AQo, AJs',
    },
    'UTG+1': {
      name: 'UTG+1',
      description: 'Second to act preflop. Still a very early position.',
      vpipRange: { min: 14, max: 20 },
      strategy: [
        'Slightly wider than UTG but still tight',
        'Add hands like 88, ATs, KQs',
        'Fold speculative hands',
      ],
      commonMistakes: [
        'Opening too wide',
        'Not respecting UTG raises',
      ],
      openingHands: 'AA-88, AKs-ATs, KQs, AKo-AJo, KQo',
    },
    'MP': {
      name: 'Middle Position',
      description: 'Middle of the table. Can start to loosen up slightly.',
      vpipRange: { min: 16, max: 24 },
      strategy: [
        'Wider than early positions',
        'Can add suited connectors like 87s, 76s',
        'Still respect raises from earlier positions',
      ],
      commonMistakes: [
        'Getting too loose',
        'Calling 3-bets without a plan',
      ],
      openingHands: 'AA-66, AKs-A9s, KQs-KTs, QJs-QTs, JTs, T9s, AKo-ATo, KQo-KJo',
    },
    'CO': {
      name: 'Cutoff (CO)',
      description: 'Second-best position. One off the Button with great steal opportunities.',
      vpipRange: { min: 22, max: 32 },
      strategy: [
        'Open wide when folded to you',
        'Steal blinds frequently',
        'Add all suited connectors, suited aces',
        '3-bet lighter vs late position opens',
      ],
      commonMistakes: [
        'Not stealing enough',
        'Playing too tight when folded to you',
        'Not defending wide enough vs BTN 3-bets',
      ],
      openingHands: 'All pairs, A2s+, K9s+, Q9s+, J9s+, T8s+, 97s+, 87s, 76s, 65s, ATo+, KTo+, QJo',
    },
    'BTN': {
      name: 'Button (BTN)',
      description: 'The BEST position. You act last on every postflop street.',
      vpipRange: { min: 35, max: 50 },
      strategy: [
        'Open VERY wide when folded to you',
        'Steal blinds aggressively',
        'Can play any suited hand',
        'Position advantage is huge - exploit it!',
        'Call 3-bets wider due to position',
      ],
      commonMistakes: [
        'Not stealing enough from the blinds',
        'Folding too much to 3-bets',
        'Not value betting thin enough',
      ],
      openingHands: 'All pairs, all suited aces, all suited kings, all suited connectors, any broadway, K5o+, Q8o+, J9o+',
    },
    'SB': {
      name: 'Small Blind (SB)',
      description: 'Worst postflop position - always out of position.',
      vpipRange: { min: 28, max: 42 },
      strategy: [
        '3-bet or fold is the standard approach',
        'Avoid completing the blind (usually -EV)',
        'When you open, go larger (3x or more)',
        'Defend vs BTN steals but carefully',
      ],
      commonMistakes: [
        'Completing the blind too often',
        'Flatting opens out of position',
        'Not 3-betting enough',
      ],
      openingHands: 'When BTN folds, open wide. Vs opens, 3-bet premium hands + some bluffs like A5s, 76s, or fold.',
    },
    'BB': {
      name: 'Big Blind (BB)',
      description: 'You get a discount to see the flop, so defend wider.',
      vpipRange: { min: 35, max: 55 },
      strategy: [
        'Defend wide vs late position opens',
        'You have a discount - exploit it',
        '3-bet strong hands, especially vs BTN',
        'Check-raise flops with strong hands and draws',
      ],
      commonMistakes: [
        'Not defending wide enough',
        'Folding too much to c-bets',
        'Not check-raising enough',
      ],
      openingHands: 'N/A - you defend rather than open. Defend with a very wide range vs BTN/SB opens, tighter vs EP.',
    },
  };
  
  if (!position || !positionGuides[position]) {
    return {
      type: 'text',
      title: 'Position Strategy',

      content: '**Position is POWER in poker!**\n\n' +
        'Acting last gives you more information and control.\n\n' +
        '**Positions (Best to Worst):**\n' +
        '1.  Button (BTN) - Best position\n' +

        '2.  Cutoff (CO) - Second best\n' +

        '3.  Middle Position (MP)\n' +

        '4. UTG+1 - Early position\n' +
        '5. UTG - Second worst\n' +
        '6. Blinds (SB/BB) - Worst positions\n\n' +
        'Which position do you want to learn about?',
      followUpQuestions: ['BTN strategy', 'CO strategy', 'How to play from UTG'],
    };
  }
  
  const guide = positionGuides[position];
  
  let content = `**${guide.name}**\n\n`;
  content += `*${guide.description}*\n\n`;
  content += `**Recommended VPIP:** ${guide.vpipRange.min}-${guide.vpipRange.max}%\n\n`;
  
  content += `**Strategy:**\n`;
  guide.strategy.forEach(s => content += `• ${s}\n`);
  content += '\n';
  
  content += `**Opening Hands:**\n${guide.openingHands}\n\n`;
  
  content += `**Common Mistakes:**\n`;
  guide.commonMistakes.forEach(m => content += ` ${m}\n`);

  
  return {
    type: 'text',
    title: `${guide.name} Strategy`,

    content,
    followUpQuestions: [
      `Give me a ${position} scenario`,
      position === 'BTN' ? 'CO strategy' : 'BTN strategy',
      'What is position?',
    ],
  };
}

// ============================================================================
// SCENARIO RESPONSE (ENHANCED)
// ============================================================================

function generateScenarioResponse(
  concept?: ScenarioConcept,
  difficulty?: ScenarioDifficulty,
  report?: TrainingReport
): CoachResponse {
  // If user asked to practice their weak area and we have a report, determine concept from leaks
  if (!concept && report) {
    const leaks = detectLeaks(report);
    if (leaks.length > 0) {
      const topLeak = leaks[0];
      if (topLeak.category === 'preflop') {
        concept = 'preflop-ranges';
      } else if (topLeak.category === 'postflop' || topLeak.id.includes('cbet')) {
        concept = 'continuation-bet';
      } else if (topLeak.category === 'aggression') {
        concept = 'value-betting';
      }
    }
  }
  
  let scenario: Scenario | undefined;
  
  if (concept) {
    const scenarios = getScenariosByConcept(concept);
    if (difficulty) {
      const filtered = scenarios.filter(s => s.difficulty === difficulty);
      scenario = filtered[Math.floor(Math.random() * filtered.length)] || scenarios[0];
    } else {
      scenario = scenarios[Math.floor(Math.random() * scenarios.length)];
    }
  }
  
  if (!scenario) {
    scenario = getRandomScenario(difficulty);
  }
  
  if (!scenario) {
    return {
      type: 'text',
      content: 'Sorry, I couldn\'t find a matching scenario. Try:\n' +
        '• "Give me a pot odds scenario"\n' +
        '• "Practice bluffing"\n' +
        '• "Beginner preflop scenario"',
      followUpQuestions: ['Random scenario', 'Practice pot odds', 'Practice c-betting'],
    };
  }
  
  const display = formatScenarioForDisplay(scenario);
  
  return {
    type: 'scenario',
    title: `${scenario.title}`,

    content: `**Concept:** ${scenario.concept.replace(/-/g, ' ').toUpperCase()}\n` +
      `**Difficulty:** ${scenario.difficulty.charAt(0).toUpperCase() + scenario.difficulty.slice(1)}\n\n` +
      `**Your Hand:** ${display.hand}\n` +
      `**Position:** ${display.position}\n` +
      `**Board:** ${display.board}\n` +
      `**Pot:** ${display.pot}\n\n` +
      `**Situation:**\n${display.action}\n\n` +
      `*What do you do? Fold, Call, Raise, or All-in?*`,
    scenario,
    followUpQuestions: ['I fold', 'I call', 'I raise'],
  };
}

// ============================================================================
// RANGE RESPONSE
// ============================================================================

function generateRangeResponse(position?: string): CoachResponse {
  if (!position) {
    return {
      type: 'text',
      content: 'Which position would you like the opening range for?\n\n' +
        '• UTG (tightest)\n' +
        '• MP (middle position)\n' +
        '• CO (cutoff)\n' +
        '• BTN (button - widest)\n' +
        '• SB (small blind)\n' +
        '• BB (big blind defense)',
      followUpQuestions: ['BTN range', 'UTG range', 'CO range'],
    };
  }
  
  const ranges: Record<string, { hands: string; percentage: string; notes: string }> = {
    'UTG': {
      hands: '**Premium Only:**\nAA, KK, QQ, JJ, TT, 99\nAKs, AQs, AJs, KQs\nAKo, AQo',
      percentage: '~10-12%',
      notes: 'Very tight! Only hands that play well multiway.',
    },
    'UTG+1': {
      hands: '**Strong Hands:**\nAA-88\nAKs-ATs, KQs, KJs\nAKo-AJo, KQo',
      percentage: '~12-15%',
      notes: 'Slightly wider but still tight.',
    },
    'MP': {
      hands: '**Solid Range:**\nAA-66\nAKs-A9s, KQs-KTs, QJs-QTs, JTs, T9s\nAKo-ATo, KQo-KJo, QJo',
      percentage: '~15-18%',
      notes: 'Can add suited connectors.',
    },
    'CO': {
      hands: '**Wide Range:**\nAll pairs (22+)\nA2s+, K9s+, Q9s+, J9s+, T8s+\n97s+, 87s, 76s, 65s\nATo+, KTo+, QTo+, JTo',
      percentage: '~25-30%',
      notes: 'Steal the blinds! Open wide.',
    },
    'BTN': {
      hands: '**Very Wide:**\nAll pairs\nAll suited Aces, All suited Kings\nAll suited connectors (32s+)\nAll suited gappers (42s+)\nK5o+, Q8o+, J9o+, T9o',
      percentage: '~40-50%',
      notes: 'The best position - play almost anything!',
    },
    'SB': {
      hands: '**Polarized (3-bet or fold vs opens):**\n3-bet Value: AA-TT, AKs-AJs, KQs, AKo-AQo\n3-bet Bluff: A5s-A2s, K5s-K2s, 76s-54s\n\n**Opening (when folded to):** Similar to CO',
      percentage: '~35-40% vs BTN open',
      notes: 'Avoid flatting - you\'re out of position.',
    },
    'BB': {
      hands: '**Defense Range (vs BTN open):**\nAll pairs\nAll suited aces\nAll suited broadways\nMost suited connectors\nOffsuit broadways (QJo+)\n\n**3-bet:** AA-QQ, AKs-AQs, AKo',
      percentage: '~40-50% defense',
      notes: 'You have a discount - defend wide!',
    },
  };
  
  const range = ranges[position] || ranges['BTN'];
  
  return {
    type: 'text',
    title: `🃏 ${position} Opening Range`,
    content: `**Position:** ${position}\n` +
      `**Range Width:** ${range.percentage}\n\n` +
      `${range.hands}\n\n` +
      `**Notes:** ${range.notes}`,
    followUpQuestions: [
      `${position} strategy tips`,
      position === 'BTN' ? 'CO range' : 'BTN range',
      `Practice ${position} scenarios`,
    ],
  };
}

// ============================================================================
// ODDS & EQUITY RESPONSES
// ============================================================================

function generateOddsResponse(potSize?: number, betSize?: number): CoachResponse {
  if (!potSize || !betSize) {
    return {
      type: 'calculation',
      title: 'Pot Odds Calculator',

      content: 'To calculate pot odds, I need:\n\n' +
        '• The pot size\n' +
        '• The bet/call amount\n\n' +
        'Try: "Pot is $100, bet is $50 - what are my odds?"',
      followUpQuestions: ['Pot is $100, bet is $50', 'What is pot odds?', 'What is equity?'],
    };
  }
  
  const totalPot = potSize + betSize;
  const potOdds = (betSize / totalPot) * 100;
  const impliedOdds = potOdds * 0.8; // Rough implied odds adjustment
  
  const requiredEquity = potOdds;
  
  let content = `**Pot Odds Calculation:**\n\n`;
  content += `Pot: $${potSize}\n`;
  content += `Bet to call: $${betSize}\n`;
  content += `Total pot after call: $${totalPot}\n\n`;
  
  content += `**Your Pot Odds:** ${potOdds.toFixed(1)}%\n\n`;
  content += `**You need ${requiredEquity.toFixed(1)}% equity to call profitably.**\n\n`;
  
  content += `**Hand Equity Reference:**\n`;
  content += `• Flush draw: ~35% (Call )\n`;

  content += `• OESD: ~32% (Call )\n`;

  content += `• Gutshot: ~17% (${potOdds < 20 ? 'Call if odds < 20%' : 'Fold '})\n`;

  content += `• Overcards: ~25% (${potOdds < 25 ? 'Call ' : 'Marginal'})\n`;

  
  return {
    type: 'calculation',
    title: 'Pot Odds',

    content,
    mathBreakdown: `Pot Odds = Call / (Pot + Call)\n= $${betSize} / ($${potSize} + $${betSize})\n= ${potOdds.toFixed(1)}%`,
    followUpQuestions: ['What is equity?', 'What are implied odds?', 'Practice a pot odds scenario'],
  };
}

function generateEquityResponse(outs?: number, hand?: string, oppHand?: string): CoachResponse {
  if (!outs && !hand) {
    return {
      type: 'calculation',
      title: 'Equity Calculator',

      content: 'I can help calculate equity!\n\n' +
        '**Tell me:**\n' +
        '• Number of outs (e.g., "9 outs")\n' +
        '• Draw type (e.g., "flush draw")\n' +
        '• Hand vs hand (e.g., "AK vs QQ")\n\n',
      followUpQuestions: ['Flush draw equity', '9 outs on the flop', 'AK vs QQ'],
    };
  }
  
  if (outs) {
    const turnEquity = outs * 2.2;
    const riverEquity = outs * 4.3;
    
    let content = `**${outs} Outs:**\n\n`;
    content += `• Chance to hit on turn: **${Math.min(turnEquity, 100).toFixed(1)}%**\n`;
    content += `• Chance to hit by river: **${Math.min(riverEquity, 100).toFixed(1)}%**\n\n`;
    
    content += `**Rule of 2 and 4:**\n`;
    content += `• Multiply outs by 2 for one card\n`;
    content += `• Multiply outs by 4 for two cards\n\n`;
    
    content += `**Common Draws:**\n`;
    content += `• Flush draw: 9 outs (~35%)\n`;
    content += `• OESD: 8 outs (~32%)\n`;
    content += `• Gutshot: 4 outs (~17%)\n`;
    content += `• Combo draw: 12-15 outs (~45-55%)`;
    
    return {
      type: 'calculation',
      title: 'Equity Calculation',

      content,
      mathBreakdown: `Turn: ${outs} × 2.2 = ${turnEquity.toFixed(1)}%\nRiver: ${outs} × 4.3 = ${riverEquity.toFixed(1)}%`,
      followUpQuestions: ['What are pot odds?', 'Practice a drawing hand', 'What is implied odds?'],
    };
  }
  
  return {
    type: 'text',
    content: 'Please specify your outs or draw type.',
    followUpQuestions: ['9 outs equity', 'Flush draw odds'],
  };
}

// ============================================================================
// HAND REVIEW RESPONSE
// ============================================================================

function generateHandReviewResponse(
  handNumber?: number,
  decisions?: LoggedDecision[],
  report?: TrainingReport
): CoachResponse {
  // If asking about mistakes without specific hand
  if (!handNumber && report) {
    // Find worst decisions
    if (decisions && decisions.length > 0) {
      const mistakes = decisions.filter(d => d.evaluation && d.evaluation.score < 5);
      if (mistakes.length > 0) {
        const worstMistake = mistakes.sort((a, b) => 
          (a.evaluation?.score || 0) - (b.evaluation?.score || 0)
        )[0];
        
        let content = `**Your Biggest Mistake:**\n\n`;
        content += `**Hand #${worstMistake.handNumber}**\n`;
        content += `You: ${worstMistake.action} ${worstMistake.amount ? `$${worstMistake.amount}` : ''}\n`;
        content += `Position: ${worstMistake.position}\n\n`;
        
        if (worstMistake.evaluation) {
          content += `**Score:** ${worstMistake.evaluation.score}/10\n`;
          content += `**Feedback:** ${worstMistake.evaluation.feedback}\n`;
        }
        
        return {
          type: 'hand_review',
          title: `Biggest Mistake - Hand #${worstMistake.handNumber}`,

          content,
          followUpQuestions: ['How do I fix this?', 'Show me another mistake', 'Give me a similar scenario'],
        };
      }
    }
    
    // If no specific decisions, show general weaknesses
    if (report.topWeaknesses.length > 0) {
      let content = `**Your Main Weaknesses:**\n\n`;
      report.topWeaknesses.forEach((w, i) => {
        content += `${i + 1}. ${w}\n`;
      });
      
      return {
        type: 'text',
        title: 'Areas to Improve',

        content,
        followUpQuestions: ['Find my leaks', 'Create improvement plan', 'Practice my weak area'],
      };
    }
  }
  
  if (!handNumber || !decisions || decisions.length === 0) {
    return {
      type: 'text',
      content: 'To review a specific hand, I need the hand number from your session.\n\n' +
        'Try: "Review hand #5" or "What were my biggest mistakes?"',
      followUpQuestions: ['What were my mistakes?', 'Review my session', 'Give me a scenario'],
    };
  }
  
  const handDecisions = decisions.filter(d => d.handNumber === handNumber);
  if (handDecisions.length === 0) {
    const minHand = Math.min(...decisions.map(d => d.handNumber));
    const maxHand = Math.max(...decisions.map(d => d.handNumber));
    return {
      type: 'text',
      content: `Hand #${handNumber} not found. Your session had hands #${minHand} to #${maxHand}.`,
      followUpQuestions: [`Review hand #${minHand}`, 'What were my mistakes?'],
    };
  }
  
  let content = `**Hand #${handNumber} Review:**\n\n`;
  
  handDecisions.forEach(dec => {
    content += `**${dec.street.toUpperCase()}:** ${dec.action}`;
    if (dec.amount) content += ` $${dec.amount}`;
    content += `\n`;
    content += `Position: ${dec.position}\n`;
    
    if (dec.evaluation) {
      const emoji = dec.evaluation.score >= 7 ? '' : dec.evaluation.score >= 5 ? '' : '';

      content += `${emoji} Score: ${dec.evaluation.score}/10\n`;
      content += `${dec.evaluation.feedback}\n`;
    }
    content += '\n';
  });
  
  return {
    type: 'hand_review',
    title: `Hand #${handNumber} Review`,

    content,
    followUpQuestions: ['Show me another hand', 'What should I have done?', 'Practice a similar spot'],
  };
}

// ============================================================================
// SHOULD I RESPONSE
// ============================================================================

function generateShouldIResponse(parsed: ParsedQuery, currentHand?: LoggedDecision): CoachResponse {
  const action = parsed.extractedEntities.action;
  
  return {
    type: 'text',
    title: 'Decision Help',

    content: `Without seeing your specific hand and situation, here's general ${action || 'decision'} advice:\n\n` +
      '**When to FOLD:**\n' +
      '• You have a weak hand with no draw\n' +
      '• Pot odds don\'t justify calling\n' +
      '• You\'re beat and have no equity\n\n' +
      '**When to CALL:**\n' +
      '• You have pot odds for your draw\n' +
      '• You have a medium-strength hand worth seeing another card\n' +
      '• You\'re trapping with a monster\n\n' +
      '**When to RAISE:**\n' +
      '• You have a strong hand - get value!\n' +
      '• You\'re semi-bluffing with a draw\n' +
      '• You want to take down the pot now\n\n' +
      '*Give me more details about your hand for specific advice!*',
    followUpQuestions: [
      'Give me a scenario to practice',
      'What are pot odds?',
      'When should I bluff?',
    ],
  };
}

// ============================================================================
// OPPONENT ANALYSIS RESPONSE
// ============================================================================

function generateOpponentAnalysisResponse(query: string): CoachResponse {
  const lower = query.toLowerCase();
  
  let opponentType = '';
  if (lower.includes('fish') || lower.includes('whale')) opponentType = 'fish';
  else if (lower.includes('nit')) opponentType = 'nit';
  else if (lower.includes('tag')) opponentType = 'tag';
  else if (lower.includes('lag')) opponentType = 'lag';
  else if (lower.includes('maniac')) opponentType = 'maniac';
  else if (lower.includes('calling station')) opponentType = 'calling_station';
  else if (lower.includes('rock')) opponentType = 'rock';
  
  const strategies: Record<string, { label: string; strategy: string[] }> = {
    'fish': {
      label: 'Fish',

      strategy: [
        'Value bet relentlessly - they call with anything',
        'NEVER bluff - they don\'t fold',
        'Bet bigger for value - they\'ll still call',
        'Isolate them preflop',
        'Play position and wait for strong hands',
      ],
    },
    'nit': {
      label: 'Nit',

      strategy: [
        'Steal their blinds constantly',
        'Fold to their raises without premium hands',
        'Bluff them on scary boards',
        'Don\'t pay off their big bets',
        'They only have it when they bet big',
      ],
    },
    'tag': {
      label: 'TAG',

      strategy: [
        'Play straightforward poker',
        'Avoid big pots without strong hands',
        'Look for small edges in 3-bet pots',
        'They\'re tough to exploit - focus on other players',
      ],
    },
    'lag': {
      label: 'LAG',

      strategy: [
        'Tighten up preflop',
        'Call down lighter than normal',
        'Let them bluff into you',
        'Trap with strong hands',
        'Don\'t try to outbluff them',
      ],
    },
    'maniac': {
      label: '🃏 Maniac',
      strategy: [
        'Tighten way up preflop',
        'Call down with medium-strength hands',
        'Let them hang themselves',
        'Don\'t bluff - they\'ll call or raise',
        'Value bet thinner than usual',
      ],
    },
    'calling_station': {
      label: 'Calling Station',

      strategy: [
        'NEVER bluff - they will call',
        'Value bet every street with any made hand',
        'Bet bigger for value',
        'Don\'t try fancy plays',
        'Straightforward value extraction',
      ],
    },
    'rock': {
      label: 'Rock',

      strategy: [
        'Steal their blinds every time',
        'Fold to any aggression without the nuts',
        'Bluff scary boards',
        'Don\'t pay them off',
      ],
    },
  };
  
  const strat = strategies[opponentType];
  
  if (!strat) {
    return {
      type: 'text',
      title: 'Opponent Analysis',

      content: 'Which player type do you want to exploit?\n\n' +
        '•  Fish - Loose passive\n' +

        '•  Calling Station - Never folds\n' +

        '•  Nit - Very tight\n' +

        '•  Rock - Ultra tight\n' +

        '•  TAG - Tight aggressive\n' +

        '•  LAG - Loose aggressive\n' +

        '• 🃏 Maniac - Hyper aggressive',
      followUpQuestions: ['How to beat fish', 'How to beat nits', 'How to beat LAGs'],
    };
  }
  
  let content = `**Exploiting ${strat.label}:**\n\n`;
  strat.strategy.forEach((s, i) => {
    content += `${i + 1}. ${s}\n`;
  });
  
  return {
    type: 'text',
    title: `How to Beat ${strat.label}`,

    content,
    followUpQuestions: [
      `Give me a scenario vs ${opponentType}`,
      'What is VPIP?',
      opponentType === 'fish' ? 'How to beat nits' : 'How to beat fish',
    ],
  };
}

// ============================================================================
// BET SIZING RESPONSE
// ============================================================================

function generateBetSizingResponse(query: string): CoachResponse {
  return {
    type: 'text',
    title: 'Bet Sizing Guide',

    content: '**Bet Sizing by Situation:**\n\n' +
      '**C-Bet (Continuation Bet):**\n' +
      '• Dry board (K72r): 25-33% pot\n' +
      '• Medium board (QJ4): 50% pot\n' +
      '• Wet board (JT9): 66-75% pot\n\n' +
      '**Value Betting:**\n' +
      '• Thin value: 25-40% pot\n' +
      '• Standard value: 50-75% pot\n' +
      '• Thick value (vs calling station): 75-100% pot\n\n' +
      '**Bluffing:**\n' +
      '• Use same sizes as value bets (balance!)\n' +
      '• Smaller on earlier streets\n' +
      '• Can go bigger on river\n\n' +
      '**3-Bet Sizing:**\n' +
      '• In position: 2.5-3x the open\n' +
      '• Out of position: 3-4x the open\n' +
      '• Vs fish: Bigger (4x+)',
    followUpQuestions: [
      'Practice bet sizing',
      'What is a c-bet?',
      'When to overbet?',
    ],
  };
}

// ============================================================================
// GENERAL ADVICE RESPONSE
// ============================================================================

function generateGeneralAdviceResponse(query: string): CoachResponse {
  const lower = query.toLowerCase();
  
  if (lower.includes('beginner') || lower.includes('start') || lower.includes('new')) {
    return {
      type: 'text',
      title: 'Beginner\'s Guide',

      content: '**Top 5 Tips for New Players:**\n\n' +
        '**1⃣ Play Tight**\n' +

        'Only play strong starting hands. Fold junk like 7-2, J-4, K-3.\n\n' +
        '**2⃣ Position is Power**\n' +

        'Play more hands on the Button, fewer in early position.\n\n' +
        '**3⃣ Bet Your Good Hands**\n' +

        'When you have top pair or better, BET! Don\'t slow-play.\n\n' +
        '**4⃣ Don\'t Chase Bad Draws**\n' +

        'Learn pot odds. Fold gutshots to big bets.\n\n' +
        '**5⃣ Pay Attention**\n' +

        'Watch opponents. Who bluffs? Who only bets with monsters?',
      followUpQuestions: [
        'What hands should I play?',
        'Explain position',
        'Practice a beginner scenario',
      ],
    };
  }
  
  if (lower.includes('bluff')) {
    return {
      type: 'text',
      title: 'Bluffing Guide',

      content: '**When to Bluff:**\n\n' +
        ' **Good bluff spots:**\n' +

        '• Scare cards hit (aces, flush completes)\n' +
        '• You can represent a strong hand\n' +
        '• Opponent showed weakness (checked multiple times)\n' +
        '• Opponent is capable of folding\n' +
        '• You have blockers to their calling range\n\n' +
        ' **Bad bluff spots:**\n' +

        '• Against calling stations\n' +
        '• Multiway pots (too many players)\n' +
        '• When you have showdown value\n' +
        '• Board favors opponent\'s range\n' +
        '• Opponent is short-stacked',
      followUpQuestions: [
        'Practice a bluffing scenario',
        'What is semi-bluffing?',
        'How often should I bluff?',
      ],
    };
  }
  
  if (lower.includes('value') || lower.includes('bet') || lower.includes('sizing')) {
    return generateBetSizingResponse(query);
  }
  
  // Default
  return {
    type: 'text',
    title: 'Poker Coach',

    content: 'I\'m here to help you improve your poker game! Here\'s what I can do:\n\n' +
      ' **Explain concepts**: "What is VPIP?", "Explain pot odds"\n' +

      ' **Practice scenarios**: "Give me a bluffing scenario"\n' +

      ' **Calculate odds**: "Pot is $100, bet is $50"\n' +

      ' **Analyze your play**: "Review my session", "Find my leaks"\n' +

      ' **Create plans**: "Make me an improvement plan"\n' +

      '🃏 **Opening ranges**: "What hands from UTG?"\n' +
      ' **Opponent tips**: "How to beat fish"\n\n' +

      '*Ask me anything about poker!*',
    followUpQuestions: [
      'Review my session',
      'Give me a scenario',
      'Beginner tips',
    ],
  };
}

// ============================================================================
// PLATFORM HELP RESPONSE
// ============================================================================

function generatePlatformHelpResponse(term?: string): CoachResponse {
  if (!term) {
    return {
      type: 'text',
      content: '**Welcome to PokerSweepStars! **\n\n' +

        'Here\'s what I can help you with:\n\n' +
        '**Currencies:**\n' +
        '• PSS Coins - Redeemable currency\n' +
        '• PlayCoins - Free practice chips\n\n' +
        '**Features:**\n' +
        '• Training Mode - Learn & earn PSS Coins\n' +
        '• Daily Challenges - 5 scenarios for bonus rewards\n' +
        '• Leaderboards - Compete for weekly prizes\n\n' +
        '**Account:**\n' +
        '• KYC - Identity verification for withdrawals\n' +
        '• Referrals - Earn by inviting friends\n\n' +
        'What would you like to know more about?',
      followUpQuestions: [
        'How do I earn PSS Coins?',
        'What are PlayCoins?',
        'How does training work?',
        'How do I redeem?',
      ],
    };
  }
  
  // Look up in platform knowledge
  const normalizedTerm = term.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  const knowledge = PLATFORM_KNOWLEDGE[normalizedTerm] || PLATFORM_KNOWLEDGE[term];
  
  if (knowledge) {
    let content = `**${knowledge.title}**\n\n${knowledge.description}`;
    
    if (knowledge.details && knowledge.details.length > 0) {
      content += '\n\n**Details:**\n' + knowledge.details.map(d => `• ${d}`).join('\n');
    }
    
    if (knowledge.tips && knowledge.tips.length > 0) {
      content += '\n\n** Tips:**\n' + knowledge.tips.map(t => `• ${t}`).join('\n');

    }
    
    return {
      type: 'text',
      content,
      followUpQuestions: knowledge.relatedTopics?.map(t => `What is ${t.replace(/_/g, ' ')}?`) || [],
    };
  }
  
  // Fallback - check poker glossary
  const glossaryTerm = Object.entries(POKER_GLOSSARY).find(
    ([key]) => key.toLowerCase() === normalizedTerm || key.toLowerCase() === term
  );
  
  if (glossaryTerm) {
    const [, entry] = glossaryTerm;
    return {
      type: 'glossary',
      title: entry.term,
      content: entry.simpleDefinition || entry.definition,
      details: entry.example,
      relatedTerms: entry.relatedTerms,
      followUpQuestions: entry.relatedTerms?.slice(0, 3).map(t => `What is ${t}?`),
    };
  }
  
  return {
    type: 'text',
    content: `I don't have specific info about "${term}", but I can help with:\n\n` +
      '• PSS Coins & PlayCoins\n' +
      '• Training & Challenges\n' +
      '• Withdrawals & Deposits\n' +
      '• KYC Verification\n' +
      '• Referrals & Agents\n\n' +
      'Try asking about one of these topics!',
    followUpQuestions: ['What are PSS Coins?', 'How does training work?', 'How do I redeem?'],
  };
}

// ============================================================================
// TRAINING REWARDS RESPONSE
// ============================================================================

function generateTrainingRewardsResponse(): CoachResponse {
  return {
    type: 'text',
    title: 'Training Rewards System',

    content: '**Earn PSS Coins While You Learn!**\n\n' +
      '**Per Scenario Rewards:**\n' +
      '• Beginner: $0.01 PSS Coins per correct answer\n' +
      '• Intermediate: $0.02 PSS Coins per correct answer\n' +
      '• Advanced: $0.05 PSS Coins per correct answer\n' +
      '• Expert: $0.10 PSS Coins per correct answer\n\n' +
      '**Daily Challenge Bonus:**\n' +
      '• Complete all 5: $0.15 PSS Coins\n' +
      '• Get all 5 correct: Extra $0.25 PSS Coins!\n\n' +
      '**Streak Multipliers:**\n' +
      '• 3 days: 1.25x rewards\n' +
      '• 7 days: 1.5x rewards\n' +
      '• 14 days: 1.75x rewards\n' +
      '• 30 days: 2x rewards!\n\n' +
      '**Concept Mastery Bonuses:**\n' +
      '• First concept mastered: $0.50 PSS Coins\n' +
      '• Each additional: $0.25 PSS Coins\n' +
      '• Master ALL concepts: $5.00 PSS Coins bonus!\n\n' +
      ' *Tip: Complete daily challenges to build your streak!*',

    followUpQuestions: [
      'Start daily challenge',
      'Give me a scenario',
      'What is my streak?',
    ],
  };
}

// ============================================================================
// DAILY CHALLENGE RESPONSE
// ============================================================================

function generateDailyChallengeResponse(): CoachResponse {
  return {
    type: 'text',
    title: 'Daily Training Challenge',

    content: '**Today\'s Challenge: 5 Scenarios**\n\n' +
      'Complete 5 training scenarios to earn bonus PSS Coins!\n\n' +
      '**Rewards:**\n' +
      '• Complete all 5: $0.15 PSS Coins\n' +
      '• Perfect score (5/5): Extra $0.25 PSS Coins!\n' +
      '• Streak bonus: Up to 2x multiplier\n\n' +
      '**How It Works:**\n' +
      '1. Scenarios are based on your skill level\n' +
      '2. Mix of concepts (preflop, postflop, math)\n' +
      '3. Resets daily at midnight UTC\n' +
      '4. Maintain streaks for bigger rewards!\n\n' +
      'Ready to start? Ask me for a scenario!',
    followUpQuestions: [
      'Give me a scenario',
      'Practice pot odds',
      'Practice bluffing',
    ],
  };
}

// ============================================================================
// SHORT STACK RESPONSE
// ============================================================================

function generateShortStackResponse(stackSize?: number): CoachResponse {
  const bb = stackSize || 15;
  
  // Push/fold ranges based on stack size
  const getPushRange = (position: string, bbCount: number): string => {
    if (bbCount <= 10) {
      switch (position) {
        case 'BTN': return 'Any pair, A2+, K2s+, K7o+, Q5s+, Q9o+, J7s+, JTo, T7s+, 97s+, 87s, 76s, 65s';
        case 'CO': return 'Any pair, A2+, K5s+, K9o+, Q8s+, QTo+, J8s+, JTo, T8s+, 98s';
        case 'MP': return '22+, A2s+, A7o+, K9s+, KTo+, QTs+, JTs';
        case 'UTG': return '33+, A8s+, ATo+, KTs+, KQo, QJs';
        default: return '22+, A2s+, A8o+, K9s+, QTs+';
      }
    } else if (bbCount <= 15) {
      switch (position) {
        case 'BTN': return '22+, A2+, K2s+, K8o+, Q7s+, QTo+, J8s+, JTo, T8s+, 98s, 87s';
        case 'CO': return '22+, A2s+, A5o+, K7s+, KTo+, Q9s+, QJo, J9s+, T9s';
        case 'MP': return '44+, A5s+, A9o+, KTs+, KQo, QJs, JTs';
        case 'UTG': return '66+, A9s+, AJo+, KQs';
        default: return '55+, A7s+, ATo+, KJs+, QJs';
      }
    } else {
      switch (position) {
        case 'BTN': return '22+, A2s+, A7o+, K8s+, KTo+, Q9s+, QJo, J9s+, T9s, 98s';
        case 'CO': return '55+, A2s+, A9o+, KTs+, QJs, JTs';
        case 'MP': return '77+, A9s+, AJo+, KQs';
        case 'UTG': return '99+, ATs+, AQo+, KQs';
        default: return '77+, A9s+, ATo+, KJs+';
      }
    }
  };
  
  return {
    type: 'text',
    title: `${bb}BB Short Stack Strategy`,

    content: `**Push/Fold Ranges at ${bb} Big Blinds:**\n\n` +
      `**Button (BTN):**\n${getPushRange('BTN', bb)}\n\n` +
      `**Cutoff (CO):**\n${getPushRange('CO', bb)}\n\n` +
      `**Middle Position (MP):**\n${getPushRange('MP', bb)}\n\n` +
      `**Under the Gun (UTG):**\n${getPushRange('UTG', bb)}\n\n` +
      '**Key Principles:**\n' +
      `• At ${bb}BB, you're in push/fold mode - no min-raising!\n` +
      '• Position is everything - push wider on the button\n' +
      '• Fold equity matters - don\'t wait for premium hands\n' +
      '• Any ace is usually a push from late position\n\n' +
      ' *Remember: A bad shove is often better than blinding out!*',

    followUpQuestions: [
      `What about ${bb - 5}BB?`,
      'ICM bubble play',
      'Give me a short stack scenario',
    ],
  };
}

// ============================================================================
// TOURNAMENT ICM RESPONSE
// ============================================================================

function generateTournamentICMResponse(query: string): CoachResponse {
  const lowerQuery = query.toLowerCase();
  
  // Determine specific ICM topic
  if (lowerQuery.includes('bubble')) {
    return {
      type: 'text',
      title: 'Bubble Strategy - ICM Pressure',

      content: '**Playing the Tournament Bubble:**\n\n' +
        '**Big Stack (40+ BB):**\n' +
        '• Apply maximum pressure on medium stacks\n' +
        '• Attack short stacks\' blinds relentlessly\n' +
        '• Avoid big confrontations with other big stacks\n' +
        '• Your chips are weapons - use them!\n\n' +
        '**Medium Stack (15-40 BB):**\n' +
        '• Play TIGHT - avoid risky spots\n' +
        '• Let short stacks bust first\n' +
        '• Only play premium hands vs big stacks\n' +
        '• Steal from other medium stacks carefully\n\n' +
        '**Short Stack (<15 BB):**\n' +
        '• Find a spot to shove before you blind out\n' +
        '• Look for fold equity opportunities\n' +
        '• Any ace is usually good enough\n' +
        '• Better to shove with K9s than blind down to 5BB\n\n' +
        '**ICM Principle:** The value of chips changes near the money. ' +
        'A chip lost is worth MORE than a chip won. Survival matters!',
      followUpQuestions: [
        'Final table strategy',
        'Short stack push range',
        'Give me a bubble scenario',
      ],
    };
  }
  
  if (lowerQuery.includes('final table')) {
    return {
      type: 'text',
      title: 'Final Table Strategy',

      content: '**Final Table ICM Considerations:**\n\n' +
        '**Pay Jump Awareness:**\n' +
        '• Each elimination means more money for everyone\n' +
        '• Big pay jumps = tighter play\n' +
        '• Don\'t risk your tournament on marginal spots\n\n' +
        '**Chip Leader Strategy:**\n' +
        '• Apply pressure but don\'t punt your lead\n' +
        '• Target medium stacks who can\'t call\n' +
        '• Big pots only vs short stacks\n\n' +
        '**Short Stack Strategy:**\n' +
        '• Look for double-up spots\n' +
        '• Don\'t ladder just to ladder (unless huge pay jumps)\n' +
        '• Push equity is your friend\n\n' +
        '**Medium Stack Strategy:**\n' +
        '• Avoid the chip leader unless you\'re ahead\n' +
        '• Attack other medium stacks cautiously\n' +
        '• Wait for good spots - don\'t force it\n\n' +
        '**3-Handed+:**\n' +
        '• Ranges widen significantly\n' +
        '• Position becomes even more crucial\n' +
        '• Don\'t be afraid to deal if offered!',
      followUpQuestions: [
        'Bubble strategy',
        'Heads up strategy',
        'Give me an ICM scenario',
      ],
    };
  }
  
  // General tournament/ICM response
  return {
    type: 'text',
    title: 'Tournament Strategy & ICM',

    content: '**Understanding ICM (Independent Chip Model):**\n\n' +
      'ICM converts your chip stack into real money equity based on:\n' +
      '• Your stack size relative to others\n' +
      '• Payout structure\n' +
      '• Players remaining\n\n' +
      '**Key ICM Concepts:**\n\n' +
      '**1. Chip Value Changes:**\n' +
      '• Your first chip is worth the most\n' +
      '• Additional chips have diminishing value\n' +
      '• Losing chips hurts more than winning helps\n\n' +
      '**2. Risk Premium:**\n' +
      '• Need MORE equity to call than in cash games\n' +
      '• Example: Need 55%+ equity to call a flip on the bubble\n\n' +
      '**3. Stack Dynamics:**\n' +
      '• Short stacks desperate = looser shoves\n' +
      '• Big stacks dangerous = tighter calls\n' +
      '• Medium stacks = most to lose, play tight\n\n' +
      '**Tournament Stages:**\n' +
      '• Early: Play like cash game (chip EV)\n' +
      '• Middle: Start considering ICM\n' +
      '• Bubble: ICM pressure is maximum\n' +
      '• ITM: Play for pay jumps\n' +
      '• Final Table: Every decision is ICM critical',
    followUpQuestions: [
      'Bubble strategy',
      'Final table play',
      'Short stack strategy',
    ],
  };
}

// ============================================================================
// PLAYER PROFILE RESPONSE
// ============================================================================

/**
 * Detailed explanations of player types/profiles
 */
const PLAYER_TYPE_EXPLANATIONS: Record<string, {
  emoji: string;
  name: string;
  description: string;
  characteristics: string[];
  whatItMeans: string;
  howToImprove: string[];
  strengths: string[];
  weaknesses: string[];
}> = {
  fish: {
    emoji: '',

    name: 'Fish',
    description: 'A recreational player who typically plays too many hands and makes fundamental errors.',
    characteristics: [
      'Plays too many hands preflop (high VPIP)',
      'Plays passively - calls too much instead of raising',
      'Often doesn\'t understand position',
      'Makes emotional decisions',
      'Chases draws without correct odds',
    ],
    whatItMeans: 'Being labeled a "fish" means your play shows patterns of a recreational player. This is NOT an insult - it just means there\'s room to improve! Everyone starts somewhere, and recognizing this is the first step to becoming a winning player.',
    howToImprove: [
      'Tighten up preflop - play fewer, stronger hands',
      'Be more aggressive when you do play - raise instead of calling',
      'Learn position - play more hands on the button, fewer from early position',
      'Study pot odds and only chase draws with correct math',
      'Focus on one concept at a time in the practice scenarios',
    ],
    strengths: ['Unpredictable', 'Sometimes hit big hands'],
    weaknesses: ['Plays too many hands', 'Too passive', 'Bleeds money over time'],
  },
  nit: {
    emoji: '',

    name: 'Nit',
    description: 'An extremely tight player who only plays premium hands.',
    characteristics: [
      'Very low VPIP (under 15%)',
      'Only enters pots with strong hands',
      'Folds to most aggression',
      'Very patient but misses value',
    ],
    whatItMeans: 'Being a "nit" means you\'re playing too tight. While discipline is good, being too tight makes you predictable and causes you to miss profitable spots.',
    howToImprove: [
      'Open up your range gradually, especially in late position',
      'Steal blinds more frequently from the button and cutoff',
      'Don\'t be afraid to defend your blinds with suited connectors',
      'Look for bluffing spots on scary boards',
    ],
    strengths: ['Disciplined', 'Rarely makes big mistakes', 'Patient'],
    weaknesses: ['Predictable', 'Easy to bluff', 'Misses value'],
  },
  tag: {
    emoji: '',

    name: 'TAG (Tight-Aggressive)',
    description: 'A solid, winning player style - tight preflop selection with aggressive postflop play.',
    characteristics: [
      'Selective preflop (VPIP 18-26%)',
      'Small gap between VPIP and PFR',
      'Aggressive postflop betting',
      'Position-aware',
    ],
    whatItMeans: 'Being a TAG is great! This is the most commonly successful playing style. You play strong hands and play them aggressively.',
    howToImprove: [
      'Continue refining your ranges by position',
      'Work on exploiting specific opponent types',
      'Study advanced concepts like range balancing',
      'Consider adding some LAG elements situationally',
    ],
    strengths: ['Solid fundamentals', 'Hard to exploit', 'Consistent winner'],
    weaknesses: ['Can become predictable to observant opponents', 'May miss thin value'],
  },
  lag: {
    emoji: '',

    name: 'LAG (Loose-Aggressive)',
    description: 'A skilled, unpredictable style - plays many hands very aggressively.',
    characteristics: [
      'Wider preflop range (VPIP 27-35%)',
      'Very aggressive postflop',
      'Applies constant pressure',
      'Wins many pots without showdown',
    ],
    whatItMeans: 'Being a LAG requires skill to pull off profitably. You\'re playing lots of hands aggressively, which can be very profitable but also high-variance.',
    howToImprove: [
      'Ensure aggression is selective and not reckless',
      'Study opponent tendencies to time aggression correctly',
      'Balance your bluffs with value hands',
      'Be aware when to slow down against calling stations',
    ],
    strengths: ['Unpredictable', 'Wins pots without showdown', 'Applies maximum pressure'],
    weaknesses: ['High variance', 'Can overplay hands', 'Susceptible to traps'],
  },
  rock: {
    emoji: '',

    name: 'Rock',
    description: 'Ultra-tight player who almost never voluntarily enters pots.',
    characteristics: [
      'Extremely low VPIP (under 10%)',
      'Only plays absolute premium hands',
      'Folds to almost any resistance',
    ],
    whatItMeans: 'Being a "rock" means you\'re playing WAY too tight. You\'re probably folding profitable hands and missing tons of value.',
    howToImprove: [
      'Gradually loosen your ranges',
      'Play more hands in position',
      'Learn to value bet thinner',
      'Recognize steal opportunities',
    ],
    strengths: ['Very disciplined', 'Rarely loses big pots'],
    weaknesses: ['Extremely predictable', 'Easy to bluff', 'Misses lots of value', 'Blinds eat away stack'],
  },
  maniac: {
    emoji: '🃏',
    name: 'Maniac',
    description: 'Hyper-aggressive player who plays almost any hand extremely aggressively.',
    characteristics: [
      'Very high VPIP (40%+)',
      'Extremely high aggression',
      'Bets and raises constantly',
      'Hard to put on a range',
    ],
    whatItMeans: 'Being a "maniac" means you\'re too aggressive. While aggression is good, uncontrolled aggression leads to big losses.',
    howToImprove: [
      'Add discipline to your game',
      'Learn to pick your spots',
      'Fold more marginal hands preflop',
      'Recognize when opponents are trapping',
    ],
    strengths: ['Unpredictable', 'Wins big pots when hitting'],
    weaknesses: ['Loses to patient opponents', 'Burns money on bad hands', 'Easy to trap'],
  },
  'calling station': {
    emoji: '',

    name: 'Calling Station',
    description: 'Passive player who calls too much and rarely folds or raises.',
    characteristics: [
      'High VPIP with low PFR (big gap)',
      'Calls with any piece of the board',
      'Rarely raises',
      'Chases every draw',
    ],
    whatItMeans: 'Being a "calling station" means you call too much. You\'re paying off opponents\' value bets and not getting value from your own hands.',
    howToImprove: [
      'Learn to fold weak hands',
      'Be more aggressive - raise instead of calling',
      'Understand when to let go of marginal hands',
      'Stop chasing draws without odds',
    ],
    strengths: ['Occasionally catches bluffs'],
    weaknesses: ['Pays off value bets', 'Gets exploited by aggression', 'Never bluffs'],
  },
  shark: {
    emoji: '',

    name: 'Shark',
    description: 'A highly skilled winning player who exploits weaker opponents.',
    characteristics: [
      'Adapts to opponents',
      'Balances ranges well',
      'Exploits tendencies',
      'Consistent winner',
    ],
    whatItMeans: 'Being a "shark" is a compliment! You\'re playing well and winning money from weaker players.',
    howToImprove: [
      'Continue studying advanced concepts',
      'Stay humble and keep learning',
      'Move up in stakes when ready',
    ],
    strengths: ['Adapts well', 'Exploits opponents', 'Consistent'],
    weaknesses: ['May need to adjust at higher stakes'],
  },
  whale: {
    emoji: '',

    name: 'Whale',
    description: 'A recreational player with deep pockets who plays for fun, not profit.',
    characteristics: [
      'Plays lots of hands',
      'Doesn\'t mind losing',
      'Gambles for entertainment',
      'Big action player',
    ],
    whatItMeans: 'Being called a "whale" usually means you play a lot and aren\'t focused on optimal strategy. If you\'re having fun, that\'s fine! If you want to improve, we can help.',
    howToImprove: [
      'Study fundamentals if you want to win more',
      'Set bankroll limits',
      'Focus on decision quality over results',
    ],
    strengths: ['Unpredictable', 'Creates action'],
    weaknesses: ['Loses money long-term', 'Makes fundamental errors'],
  },
  donkey: {
    emoji: '',

    name: 'Donkey',
    description: 'Slang for a bad player who makes many mistakes.',
    characteristics: [
      'Makes obvious errors',
      'Doesn\'t learn from mistakes',
      'Plays emotionally',
    ],
    whatItMeans: '"Donkey" or "donk" is poker slang for a bad player. Don\'t take it personally - use it as motivation to improve!',
    howToImprove: [
      'Study poker fundamentals',
      'Review your hands for mistakes',
      'Control emotions at the table',
      'Practice with training scenarios',
    ],
    strengths: ['Can get lucky in the short term'],
    weaknesses: ['Loses long-term', 'Predictably bad decisions'],
  },
  reg: {
    emoji: '',

    name: 'Reg (Regular)',
    description: 'A regular player who plays frequently and usually has solid fundamentals.',
    characteristics: [
      'Plays often',
      'Has a consistent strategy',
      'Usually winning or break-even',
    ],
    whatItMeans: 'Being a "reg" means you\'re a regular player at your stakes. You have solid fundamentals and play consistently.',
    howToImprove: [
      'Study to beat other regs',
      'Focus on thin edges',
      'Game select to find weaker players',
    ],
    strengths: ['Consistent', 'Experienced', 'Solid fundamentals'],
    weaknesses: ['May be exploitable by better regs'],
  },
  recreational: {
    emoji: '',

    name: 'Recreational Player',
    description: 'Someone who plays poker for fun rather than profit.',
    characteristics: [
      'Plays for entertainment',
      'Not focused on optimal strategy',
      'Takes shots at fun hands',
    ],
    whatItMeans: 'Being labeled "recreational" just means you play for fun. That\'s totally fine! If you want to improve, we\'re here to help.',
    howToImprove: [
      'Learn basic fundamentals',
      'Practice with training scenarios',
      'Focus on having fun while improving',
    ],
    strengths: ['Enjoying the game!'],
    weaknesses: ['May leave money on the table'],
  },
};

function generatePlayerProfileResponse(
  playerType?: string,
  report?: TrainingReport
): CoachResponse {
  // If a specific player type is mentioned
  if (playerType) {
    const typeKey = playerType.toLowerCase().trim();
    const typeInfo = PLAYER_TYPE_EXPLANATIONS[typeKey];
    
    if (typeInfo) {
      let content = `${typeInfo.emoji} **${typeInfo.name}**\n\n`;
      content += `${typeInfo.description}\n\n`;
      content += `**What it means for you:**\n${typeInfo.whatItMeans}\n\n`;
      
      content += `**Characteristics:**\n`;
      typeInfo.characteristics.forEach(c => content += `• ${c}\n`);
      content += '\n';
      
      content += `**Strengths:** ${typeInfo.strengths.join(', ')}\n`;
      content += `**Weaknesses:** ${typeInfo.weaknesses.join(', ')}\n\n`;
      
      content += `**How to improve:**\n`;
      typeInfo.howToImprove.forEach(h => content += `• ${h}\n`);
      
      // If we have report context and the user's profile matches
      if (report) {
        const userProfile = classifyPlayerType(
          report.stats.vpip || 0,
          report.stats.pfr || 0,
          report.stats.aggression_factor || 2
        );
        if (userProfile.type === typeKey) {
          content += `\n**Your stats show this profile because:**\n`;
          content += `• VPIP: ${report.stats.vpip?.toFixed(1) || 'N/A'}%\n`;
          content += `• PFR: ${report.stats.pfr?.toFixed(1) || 'N/A'}%\n`;
          content += `• Aggression Factor: ${report.stats.aggression_factor?.toFixed(1) || 'N/A'}\n`;
        }
      }
      
      return {
        type: 'text',
        content,
        followUpQuestions: [
          'How do I stop being a ' + typeKey + '?',
          'Give me a practice scenario',
          'What stats should I aim for?',
          'Create an improvement plan',
        ],
      };
    }
  }
  
  // Generic player types overview
  let content = `** Player Types in Poker**\n\n`;

  content += `Your "Player Profile" is determined by analyzing your stats (VPIP, PFR, Aggression). Here are the main types:\n\n`;
  
  const mainTypes = ['fish', 'nit', 'tag', 'lag', 'calling station', 'maniac', 'shark'];
  mainTypes.forEach(type => {
    const info = PLAYER_TYPE_EXPLANATIONS[type];
    if (info) {
      content += `${info.emoji} **${info.name}**: ${info.description.split('.')[0]}.\n\n`;
    }
  });
  
  content += `\n**Want to know more?** Ask about any specific type, like "What is a TAG?" or "How do I stop being a fish?"`;
  
  // If we have a report, show user's current profile
  if (report) {
    const userProfile = classifyPlayerType(
      report.stats.vpip || 0,
      report.stats.pfr || 0,
      report.stats.aggression_factor || 2
    );
    content += `\n\n**Your current profile:** ${userProfile.label}`;
  }
  
  return {
    type: 'text',
    content,
    followUpQuestions: [
      'What is a TAG?',
      'How do I stop being a fish?',
      'What stats should I aim for?',
      'Create an improvement plan',
    ],
  };
}

// ============================================================================
// UNKNOWN RESPONSE
// ============================================================================

function generateUnknownResponse(query: string): CoachResponse {
  return {
    type: 'text',
    content: `I'm not sure I understood that, but I'm here to help! \n\n` +

      '** Poker Concepts:**\n' +

      '"What is VPIP?" "Explain pot odds" "What does villain mean?"\n\n' +
      '** Practice Scenarios:**\n' +

      '"Give me a scenario" "Practice c-betting" "Bluffing drill"\n\n' +
      '** Math Questions:**\n' +

      '"Pot is $100, call is $25" "9 outs equity" "Flush draw odds"\n\n' +
      '** Analysis:**\n' +

      '"Review my session" "Find my leaks" "Check my VPIP"\n\n' +
      '** Tournament:**\n' +

      '"Bubble strategy" "15bb push range" "ICM pressure"\n\n' +
      '** Platform Help:**\n' +

      '"What are PSS Coins?" "How do I redeem?" "Training rewards"\n\n' +
      'Try rephrasing your question or pick one of these topics!',
    followUpQuestions: [
      'Give me a scenario',
      'What are PSS Coins?',
      'How does training work?',
      'What is pot odds?',
    ],
  };
}

// ============================================================================
// SCENARIO EVALUATOR
// ============================================================================

export function evaluateScenarioResponse(
  scenario: Scenario,
  userAction: 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'all-in',
  userSizing?: number
): {
  correct: boolean;
  evaluation: 'excellent' | 'acceptable' | 'suboptimal' | 'poor';
  feedback: string;
  lesson: string;
} {
  // Bet and raise are the same aggressive answer - staged scenarios carry
  // prior action in the pot (currentBet 0), so the table reports 'bet' for
  // what the scenario calls 'raise'. Comparing them literally failed
  // CORRECT answers as POOR.
  const norm = (a: string) => (a === 'bet' ? 'raise' : a);
  const isCorrect = norm(userAction) === norm(scenario.correctAction);

  // Honest pot-relative description of the recommended sizing range.
  const potPct = (amount: number) =>
    scenario.potSize > 0 ? Math.round((amount / scenario.potSize) * 100) : 0;
  const describeRange = (lo: number, hi: number) =>
    scenario.potSize > 0
      ? `$${lo}-$${hi} (${potPct(lo)}-${potPct(hi)}% of the pot)`
      : `$${lo}-$${hi}`;

  if (isCorrect) {
    if (scenario.correctSizing && userSizing) {
      const sizingDiff = Math.abs(userSizing - scenario.correctSizing) / scenario.correctSizing;
      if (sizingDiff > 0.3) {
        // Give a range instead of exact amount - more helpful for beginners
        const lowRange = Math.round(scenario.correctSizing * 0.8);
        const highRange = Math.round(scenario.correctSizing * 1.2);
        return {
          correct: true,
          evaluation: 'acceptable',
          feedback: `Right action! Your sizing ($${userSizing}) is a bit ${userSizing < scenario.correctSizing ? 'small' : 'large'}. A good range here is ${describeRange(lowRange, highRange)}.`,

          lesson: scenario.explanation,
        };
      }
    }

    return {
      correct: true,
      evaluation: 'excellent',
      feedback: ' Correct! ' + scenario.explanation,

      lesson: scenario.keyLesson,
    };
  }

  // Check for alternative acceptable actions
  const alternative = scenario.alternativeActions?.find(a =>
    norm(a.action.toLowerCase()) === norm(userAction)
  );

  if (alternative) {
    return {
      correct: false,
      evaluation: alternative.evaluation as any,
      feedback: alternative.explanation,
      lesson: scenario.keyLesson,
    };
  }

  // Wrong action - give helpful feedback with sizing ranges instead of exact amounts
  let sizingHint = '';
  if (scenario.correctSizing) {
    const lowRange = Math.round(scenario.correctSizing * 0.8);
    const highRange = Math.round(scenario.correctSizing * 1.2);
    sizingHint = ` around ${describeRange(lowRange, highRange)}`;
  }

  return {
    correct: false,
    evaluation: 'poor',
    feedback: ` The best play here is to ${scenario.correctAction.toUpperCase()}${sizingHint}.\n\n${scenario.explanation}`,

    lesson: scenario.keyLesson + `\n\n**Common mistake**: ${scenario.commonMistake}`,
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export const pokerCoach = {
  parseQuery,
  generateResponse,
  evaluateScenarioResponse,
  detectLeaks,
  generateImprovementPlan,
  classifyPlayerType,
  OPTIMAL_STATS,
  QUERY_PATTERNS,
};

export default pokerCoach;